// [EMERGENCY] Move to top to ensure it runs even if late-init scripts fail
setTimeout(() => {
    document.body.classList.remove('loading');
}, 3500);

import { gsap } from 'gsap/all';
import { ScrollTrigger } from 'gsap/all';
import { Draggable } from 'gsap/all';
import { MotionPathPlugin } from 'gsap/all';
import Lenis from 'lenis';
import Revealer from './revealer';
import { BlurScrollEffect } from './effects/blurScrollEffect';
import { HighlightEffect as PriceHighlight } from './effects/highlightEffect';
import { preloadImages } from './utils';
import lottie from 'lottie-web';
import { initModalCloseButton } from './modal-close-handler.js';
import SplitType from 'split-type';
import initStorytellingInflationGraph from './inflation_story.js';


// Safely register plugins
gsap.registerPlugin(ScrollTrigger, Draggable, MotionPathPlugin);

// Force remove loading class after 3.5 seconds to prevent infinite loading
// (Already moved to top, removing this duplicate block if it exists)

console.log('Index.js initializing...');

// Custom Dot Cursor
const cursor = document.querySelector('.custom-cursor');
if (cursor) {
    document.addEventListener('mousemove', (e) => {
        cursor.style.left = e.clientX + 'px';
        cursor.style.top = e.clientY + 'px';
    });
}

// Global instance for hero copy effect
let heroCopyEffect = null;

// Lenis smooth scrolling
let lenis;

const initSmoothScrolling = () => {
    lenis = new Lenis({
        lerp: 0.1,
        smooth: true,
    });

    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    // [FIX] Anchor Link Handling with Lenis
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            e.preventDefault();
            const targetId = this.getAttribute('href');
            if (targetId && targetId !== '#') {
                const targetElement = document.querySelector(targetId);
                if (targetElement) {
                    lenis.scrollTo(targetElement); // Lenis 부드러운 이동
                }
            }
        });
    });
};

// Hero Content Fade Out - Ripple/Wave effect tied to scroll
const initHeroFadeOut = () => {
    // 사용자 요청: 비디오가 등장하지 않으므로 히어로 배경 이미지와 텍스트는 항상 고정(유지)되어야 함.
    // 기존의 스크롤에 따른 페이드아웃 로직을 비활성화합니다.
    return;
};

// Show Header Animation
const showHeader = () => {
    const header = document.querySelector('.header');
    if (header) {
        gsap.to(header, {
            opacity: 1,
            y: 0, // Reset translateY
            duration: 1,
            ease: 'power3.out',
            pointerEvents: 'auto'
        });
    }
};

// Logo Click Event
const initLogoClick = () => {
    const logo = document.querySelector('.header__logo-container');
    if (logo && lenis) {
        logo.style.cursor = 'pointer';
        logo.addEventListener('click', (e) => {
            e.preventDefault();
            lenis.scrollTo(0, { duration: 2 });
        });
    }
};

// Go to Home Button (at the bottom)
const initGoHome = () => {
    const btn = document.getElementById('btn-go-home');
    if (btn && lenis) {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            lenis.scrollTo(0, { duration: 2 });
        });
    }
};

// Sound Toggle Logic
const initSoundToggle = () => {
    const btn = document.querySelector('.sound-toggle');
    const video = document.querySelector('.video-fill');
    const iconOff = document.querySelector('.icon--sound-off');
    const iconOn = document.querySelector('.icon--sound-on');

    if (!btn || !video) return;

    btn.addEventListener('click', () => {
        video.muted = !video.muted;
        if (video.muted) {
            iconOn.style.display = 'none';
            iconOff.style.display = 'block';
            btn.setAttribute('aria-label', '음소거 해제');
        } else {
            iconOff.style.display = 'none';
            iconOn.style.display = 'block';
            btn.setAttribute('aria-label', '음소거');
        }
    });

    // Also update cursor hover effect for this new button
    btn.addEventListener('mouseenter', () => {
        const cursor = document.querySelector('.custom-cursor');
        if (cursor) {
            cursor.style.transform = 'translate(-50%, -50%) scale(2.5)';
            cursor.style.opacity = '0.5';
        }
    });
    btn.addEventListener('mouseleave', () => {
        const cursor = document.querySelector('.custom-cursor');
        if (cursor) {
            cursor.style.transform = 'translate(-50%, -50%) scale(1)';
            cursor.style.opacity = '1';
        }
    });
};

// Infra (Network/Resort) Toggle Logic
const initInfraToggle = () => {
    const btns = document.querySelectorAll('.infra__toggle-btn');
    const tables = document.querySelectorAll('.infra__table-tab');
    const servicePins = document.querySelectorAll('.infra-pin--service');
    const resortPins = document.querySelectorAll('.infra-pin--resort');

    const infraSection = document.getElementById('infra');

    if (btns.length === 0) return;

    btns.forEach(btn => {
        btn.addEventListener('click', () => {
            const tabId = btn.getAttribute('data-tab');

            // 1. Update Buttons
            btns.forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            // 2. Update Tables
            tables.forEach(t => {
                t.classList.remove('active');
                if (t.id === `tab-${tabId}`) t.classList.add('active');
            });

            // 3. Update Map Pins & Section Theme
            if (tabId === 'service') {
                servicePins.forEach(p => p.classList.add('active'));
                resortPins.forEach(p => p.classList.remove('active'));
                if (infraSection) infraSection.classList.remove('light-mode');
            } else {
                servicePins.forEach(p => p.classList.remove('active'));
                resortPins.forEach(p => p.classList.add('active'));
                if (infraSection) infraSection.classList.add('light-mode');
            }

            // Refresh ScrollTrigger as heights might change
            ScrollTrigger.refresh();
        });
    });
};


// Showcase Cards Scroll Animation
const initShowcaseCardsAnimation = () => {
    const showcaseItems = document.querySelectorAll('.showcase__item');

    if (showcaseItems.length > 0) {
        showcaseItems.forEach((item, index) => {
            gsap.fromTo(item,
                {
                    y: 80,
                    opacity: 0
                },
                {
                    y: 0,
                    opacity: 1,
                    duration: 1.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: item,
                        start: 'top 85%',
                        toggleActions: 'play none none reverse'
                    },
                    delay: index * 0.15 // Stagger effect for sequential reveal
                }
            );
        });
    }
};

// Service Essence Star Animation
const initServiceEssenceAnimations = () => {
    const starsContainer = document.getElementById('stars-container');
    const starItems = Array.from(document.querySelectorAll('.star-item')).reverse();

    if (starsContainer && starItems.length > 0) {
        ScrollTrigger.create({
            trigger: starsContainer,
            start: 'top 85%',
            onEnter: () => {
                starItems.forEach((star, index) => {
                    setTimeout(() => {
                        star.classList.add('is-active');
                        const player = star.querySelector('lottie-player');
                        if (player) {
                            player.speed = 1.5; // Faster playback
                            if (typeof player.play === 'function') {
                                player.play();
                            } else {
                                player.addEventListener('ready', () => {
                                    player.speed = 1.5;
                                    player.play();
                                });
                            }
                        }
                    }, index * 100); // Snappier stagger (200ms -> 100ms)
                });
            },
            once: true
        });
    }

    // Scroll-triggered Fade In & Float Up for the 3 Frames
    const essenceFrames = document.querySelectorAll('.essence__q-box, .essence__infographic');
    essenceFrames.forEach((frame) => {
        gsap.fromTo(frame,
            { y: 80, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 1.2,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: frame,
                    start: 'top 85%', // Trigger when top of element is 85% down viewport
                    toggleActions: 'play none none reverse' // Reverses when scrolling back up
                }
            }
        );
    });
};

// Floating Card Behavior
const initFloatingCard = () => {
    const selector = '.pin';
    const pin = document.querySelector(selector);
    const hero = document.querySelector('.content');

    if (!pin) return;

    // 1. ScrollTrigger for showing and minimizing
    ScrollTrigger.create({
        start: 'top -100', // Trigger as soon as user scrolls slightly
        onEnter: () => {
            gsap.to(pin, { opacity: 1, duration: 0.8, overwrite: 'auto' });
            pin.classList.add('pin--minimized');
        },
        onLeaveBack: () => {
            // If we scroll back to very top, we might want to keep it minimized or expand 
            // but user said follow for impatient scrollers.
            pin.classList.remove('pin--minimized');
        }
    });

    if (hero) {
        ScrollTrigger.create({
            trigger: hero,
            start: 'top top-=150',
            onEnter: () => pin.classList.add('pin--minimized'),
            onLeaveBack: () => pin.classList.remove('pin--minimized'),
        });
    }

    // 3. Click to Expand (Interaction)
    const btn = pin.querySelector('.pin__button');
    if (btn) {
        btn.addEventListener('click', (e) => {
            // If minimized, prevent submit and expand instead
            if (pin.classList.contains('pin--minimized')) {
                e.preventDefault();
                pin.classList.remove('pin--minimized');
            }
        });
    }

    // 닫기 버튼: 누르면 다시 접힌 상태 (버튼 형태)로 돌아감
    const closeBtn = pin.querySelector('#btn-floating-close');
    if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            pin.classList.add('pin--minimized');
        });
    }

    // 4. Click Outside to Minimize (Close)
    document.addEventListener('click', (e) => {
        // If clicking outside the pin AND the pin is NOT minimized AND we are scrolled down (past hero trigger)
        if (!pin.contains(e.target) && !pin.classList.contains('pin--minimized') && window.scrollY > 150) {
            pin.classList.add('pin--minimized');
        }
    });

    // 2. Lag Effect (Desktop Only)
    if (window.innerWidth <= 768) return;

    let scrollTop = 0;
    let pinTop = 0;
    let lastTime;
    const lagAmount = 50;
    const maxSpeed = 100;
    const frameRate = 20;

    const updatePinPosition = (time) => {
        if (!lastTime) lastTime = time;
        let delta = time - lastTime;

        if (delta >= frameRate) {
            scrollTop = window.scrollY;
            const move = (scrollTop - pinTop) * delta / (lagAmount + delta);
            const direction = move === 0 ? 0 : move / Math.abs(move);
            pinTop = pinTop + Math.min(Math.abs(move), maxSpeed) * direction;

            // Apply transform (removed -50% centering logic)
            pin.style.transform = `translateY(${-move}px)`;

            lastTime = time;
        }
        requestAnimationFrame(updatePinPosition);
    };

    requestAnimationFrame(updatePinPosition);
};

// Video Scroll Behavior
const initVideoBehavior = () => {
    const video = document.querySelector('.video-overlay');
    const content = document.querySelector('.content');

    if (!video || !content) return;

    ScrollTrigger.create({
        trigger: content,
        start: '100px top',
        onEnter: () => {
            gsap.to(video, {
                opacity: 1, // 선명하게 유지
                mixBlendMode: 'normal',
                duration: 1.5,
                overwrite: 'auto'
            });
        },
        onLeaveBack: () => {
            // 히어로 섹션 재진입 시 영상을 더욱 어둡게(0.25) 하여 배경으로 완전히 밀어냄
            gsap.to(video, {
                opacity: 0.25,
                mixBlendMode: 'normal',
                duration: 1.5,
                overwrite: 'auto'
            });
        }
    });
};

// Preload images
console.log('Starting image preload...');
preloadImages('.layers__item-img').then(() => {
    console.log('Preload complete. Starting intro...');
    document.body.classList.remove('loading');
    let introTimeout;

    try {
        const revealer = new Revealer();
        setTimeout(() => {
            console.log('Calling revealer.reveal()');
            revealer.reveal();

            // Initialization handled by Unified Initialization block at end of file

            setTimeout(() => {
                showHeader();

                // 메인 카피 순차 등장 애니메이션 (사용자 요청: chars + edges stagger + scale)
                const copySub = document.querySelector('.copy__sub');
                const copyMain = document.querySelector('.copy__main');
                const copyDesc = document.querySelector('.copy__desc');

                const tlHero = gsap.timeline({
                    onComplete: () => {
                        // 모든 히어로 요소 노출 보장
                        gsap.set(['.copy__sub', '.copy__main', '.copy__desc', '.scroll-indicator', '.content', '.copy-container'], {
                            opacity: 1,
                            visibility: 'visible',
                            y: 0
                        });
                        showHeroVideoPopup();
                    }
                });

                // 1. Sub Copy 등장
                if (copySub) {
                    gsap.set(copySub, { opacity: 0, y: 20 });
                    tlHero.to(copySub, {
                        opacity: 1,
                        y: 0,
                        duration: 1.2, // 느리게
                        ease: 'power2.out'
                    });
                }

                // 2. Main Copy (사용자 요청 효과: chars + edges stagger + scale)
                if (copyMain) {
                    // words와 chars로 분할 (컨테이너가 숨겨진 상태에서 수행)
                    const split = new SplitType(copyMain, { types: 'words, chars' });
                    const words = split.words || [];

                    if (words.length > 0) {
                        // 모든 chars를 하나의 배열로 합침
                        const allChars = [];
                        words.forEach(word => {
                            const chars = word.querySelectorAll('.char');
                            chars.forEach(char => allChars.push(char));
                        });

                        // 글자들을 먼저 숨김
                        gsap.set(allChars, {
                            opacity: 0,
                            scale: 1.5,
                            display: 'inline-block',
                            willChange: 'transform, opacity'
                        });

                        // 이제 컨테이너를 보임 (글자들은 이미 숨겨져 있음)
                        gsap.set(copyMain, { opacity: 1, visibility: 'visible' });

                        // 문장 전체가 하나의 흐름으로 가속도감(Slow-Fast-Slow) 있게 등장
                        tlHero.fromTo(allChars, {
                            opacity: 0,
                            scale: 1.4, // 스케일 변화 소폭 완화
                            yPercent: 10 // 미세한 y축 이동 추가로 입체감 부여
                        }, {
                            opacity: 1,
                            scale: 1,
                            yPercent: 0,
                            duration: 0.8, // 가감속을 느끼기에 충분한 시간
                            ease: 'power2.inOut', // 개별 글자의 움직임 가감속
                            stagger: {
                                each: 0.03, // 글자 간 간격 소폭 증가
                                from: 'start',
                                ease: 'power2.inOut' // 문장 전체의 파장 곡선 (느리게 시작 -> 중간 가속 -> 마지막 감속)
                            }
                        }, '-=0.8');
                    }
                }

                // 3. Desc Copy 및 Scroll Indicator 등장
                if (copyDesc) {
                    gsap.set(['.copy__desc', '.scroll-indicator'], { opacity: 0, y: 20 });
                    tlHero.to(['.copy__desc', '.scroll-indicator'], {
                        opacity: 1,
                        y: 0,
                        duration: 1.2,
                        ease: 'power2.out'
                    }, '-=0.5');
                }

                // [추가] 배경 그리드(content) 등장 보장
                tlHero.to('.content', { opacity: 1, duration: 1 }, 0);

                // BlurScrollEffect는 스크롤 기반이므로 자동으로 작동

                ScrollTrigger.refresh();
                // window.scrollTo(0, 0); // Removed to prevent forced scroll to top on refresh
            }, 2000);
        }, 1000);
    } catch (err) {
        console.error('Error during Revealer initialization:', err);
    }
}).catch(err => {
    console.warn('Preload failed:', err);
    document.body.classList.remove('loading');
});
// Header Scroll Effect
const header = document.querySelector('.header');
window.addEventListener('scroll', () => {
    if (window.scrollY > 100) {
        header.classList.add('header--scrolled');
    } else {
        header.classList.remove('header--scrolled');
    }
});

// Cursor
const updateCursorHover = () => {
    const interactives = document.querySelectorAll('a, button, #splash-trigger, .header__link, .header__logo-container, #burger-trigger, #mobile-menu-close');
    interactives.forEach(el => {
        el.addEventListener('mouseenter', () => {
            cursor.style.transform = 'translate(-50%, -50%) scale(2.5)';
            cursor.style.opacity = '0.5';
        });
        el.addEventListener('mouseleave', () => {
            cursor.style.transform = 'translate(-50%, -50%) scale(1)';
            cursor.style.opacity = '1';
        });
    });
};

updateCursorHover();

// Mobile Menu (Refactored for Safari stability)
const burger = document.getElementById('burger-trigger');
const closeBtn = document.getElementById('mobile-menu-close');
const mobileItems = document.querySelectorAll('.mobile-menu__item');

if (burger) {
    burger.addEventListener('click', (e) => {
        e.preventDefault();
        document.body.classList.add('mobile-menu-active');
    });
}

if (closeBtn) {
    closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        document.body.classList.remove('mobile-menu-active');
        // Reset sub-menus when closing
        mobileItems.forEach(i => i.classList.remove('is-active'));
    });
}

mobileItems.forEach(item => {
    const mainLink = item.querySelector('.mobile-menu__link');
    const subLinks = item.querySelectorAll('.mobile-menu__sub a');

    if (mainLink) {
        mainLink.addEventListener('click', (e) => {
            const isActive = item.classList.contains('is-active');

            // Close other items
            mobileItems.forEach(otherItem => {
                if (otherItem !== item) otherItem.classList.remove('is-active');
            });

            if (!isActive) {
                e.preventDefault();
                item.classList.add('is-active');
            } else {
                // Already open, allow navigation and close menu
                document.body.classList.remove('mobile-menu-active');
            }
        });
    }

    subLinks.forEach(sub => {
        sub.addEventListener('click', () => {
            document.body.classList.remove('mobile-menu-active');
            item.classList.remove('is-active');
        });
    });
});

// ------- Highlight Effect & Text Splitting ------- //

class Text3DRevealEffect {
    constructor(el) {
        if (!el || !(el instanceof HTMLElement)) return;

        this.highlightedElement = el;
        this.createStructure();

        this.highlightedChars = this.highlightedElement.querySelectorAll('.char');
        this.highlightedWords = this.highlightedElement.querySelectorAll('.word');

        this.animationDefaults = {
            duration: 1.0,
            ease: 'power2.out'
        };

        this.initializeEffect();
    }

    createStructure() {
        const text = this.highlightedElement.innerHTML;
        const processedText = text.replace(/<br\s*\/?>/gi, ' [[br]] ');
        const words = processedText.trim().split(/\s+/);
        let newHTML = '';

        words.forEach(word => {
            if (word === '[[br]]') {
                newHTML += '<br>';
            } else {
                newHTML += `<span class="word">`;
                let isStrong = false;
                let i = 0;
                while (i < word.length) {
                    if (word.slice(i, i + 8) === '<strong>') {
                        isStrong = true;
                        i += 8;
                    } else if (word.slice(i, i + 9) === '</strong>') {
                        isStrong = false;
                        i += 9;
                    } else {
                        const char = word[i];
                        newHTML += `<span class="char">${isStrong ? `<strong>${char}</strong>` : char}</span>`;
                        i++;
                    }
                }
                newHTML += `</span> `;
            }
        });
        this.highlightedElement.innerHTML = newHTML;
    }

    initializeEffect() {
        gsap.set(this.highlightedElement, { perspective: 800, opacity: 1 });
        gsap.set(this.highlightedWords, { transformStyle: 'preserve-3d' });

        gsap.set(this.highlightedChars, {
            opacity: 0,
            z: 300,
            rotationX: -60,
            y: 30
        });
        this.scroll();
    }

    scroll() {
        ScrollTrigger.create({
            trigger: this.highlightedElement,
            start: 'top bottom',
            onEnter: () => this.animateChars(),
            onEnterBack: () => this.animateChars(),
            onLeave: () => this.resetChars(),
            onLeaveBack: () => this.resetChars()
        });
    }

    animateChars() {
        gsap.killTweensOf(this.highlightedChars);
        gsap.fromTo(this.highlightedChars, {
            opacity: 0,
            z: 300,
            rotationX: -60,
            y: 30
        },
            {
                stagger: 0.04,
                opacity: 1,
                z: 0,
                rotationX: 0,
                y: 0,
                duration: this.animationDefaults.duration,
                ease: this.animationDefaults.ease,
                overwrite: true
            });
    }

    resetChars() {
        gsap.killTweensOf(this.highlightedChars);
        gsap.set(this.highlightedChars, {
            opacity: 0,
            z: 300,
            rotationX: -60,
            y: 30
        });
    }
}

// Hero Copy Effect Removed (handled in intro animation)

// Inflation Chart Animation
// Inflation Chart Animation
const initInflationCharts = () => {
    const section = document.querySelector('.section--inflation');
    if (!section) return;

    // 1. Header Text
    // 1. Header Text
    const headerTitle = section.querySelector('.inflation__title');

    // New Message Text Elements
    const messageTitle = section.querySelector('.inflation__message-title');
    const messageBodyLines = section.querySelectorAll('.inflation__message-body p');

    if (headerTitle) {
        gsap.from(headerTitle, {
            scrollTrigger: {
                trigger: section,
                start: 'top 70%',
            },
            y: 50,
            opacity: 0,
            duration: 1,
            ease: 'power3.out'
        });
    }

    if (messageTitle) {
        gsap.from(messageTitle, {
            scrollTrigger: {
                trigger: section,
                start: 'top 65%',
            },
            y: 30,
            opacity: 0,
            duration: 1,
            delay: 0.3, // Slightly after main title
            ease: 'power3.out'
        });
    }

    if (messageBodyLines.length > 0) {
        gsap.from(messageBodyLines, {
            scrollTrigger: {
                trigger: section,
                start: 'top 65%',
            },
            y: 30,
            opacity: 0,
            duration: 1,
            stagger: 0.3,
            delay: 0.6, // Ascending delay
            ease: 'power3.out'
        });
    }





    // 2. Chart Animation
    const chartWrapper = section.querySelector('.chart-wrapper');
    if (chartWrapper) {
        ScrollTrigger.create({
            trigger: chartWrapper,
            start: 'top 75%',
            once: true, // Trigger only once
            onEnter: () => {
                const cardFear = chartWrapper.querySelector('.chart-card--fear');
                const barGroups = chartWrapper.querySelectorAll('.h-bar-group');

                // Show containers immediately so GSAP can animate them from 0
                gsap.set([cardFear, barGroups], { visibility: 'visible', opacity: 1 });

                const tl = gsap.timeline();
                tl.from(cardFear, { y: 30, opacity: 0, duration: 0.6, ease: 'power2.out' })
                    .from(barGroups, { x: -20, opacity: 0, duration: 0.5, stagger: 0.1, ease: 'power1.out' }, '-=0.3')
                    .fromTo(chartWrapper.querySelectorAll('.h-bar'),
                        { width: 0 },
                        {
                            width: (i, el) => el.style.getPropertyValue('--target-width'),
                            duration: 1.5,
                            ease: 'power3.inOut',
                            stagger: 0.15
                        },
                        '-=0.2'
                    )
                    .to(chartWrapper.querySelector('.tooltip-trigger'), { scale: 1, opacity: 1, duration: 0.6, ease: 'elastic.out(1, 0.5)' }, '-=0.2');
            }
        });
    }

    // 3. Survey Jelly Blob Slider Animation
    const surveyWrapper = section.querySelector('.survey-wrapper');
    if (surveyWrapper) {
        ScrollTrigger.create({
            trigger: surveyWrapper,
            start: 'top 80%',
            once: true,
            onEnter: () => {
                const maxVal = 74.6;
                const trackWidth = 400; // 450 - 50 = 400
                const targetX = (maxVal / 100) * trackWidth;

                // Elements
                const blobGroup = surveyWrapper.querySelector('#blob-group');
                const blobDisplay = surveyWrapper.querySelector('#blob-display');
                const blobHandle = surveyWrapper.querySelector('#blob-handle');
                const blobConnector = surveyWrapper.querySelector('#blob-connector'); // New connector
                const fillLine = surveyWrapper.querySelector('#fill-line');
                const textGroup = surveyWrapper.querySelector('#text-group');
                const blobText = surveyWrapper.querySelector('.blob-text');
                const resultText = surveyWrapper.querySelector('.survey-result-text');

                // GSAP Timeline
                const tl = gsap.timeline();

                // 1. "Blob Up": Handle pops up a big bubble
                tl.to(blobHandle, {
                    attr: { r: 12 }, // Base handle slightly larger
                    duration: 0.4,
                    ease: 'power2.out'
                })
                    // Connector stretches up
                    .to(blobConnector, {
                        attr: { cy: -48, r: 24 }, // Bridge the gap
                        duration: 0.8,
                        ease: 'elastic.out(1, 0.4)'
                    }, '-=0.2')
                    // Morph circle to bigger bubble (High floating)
                    .to(blobDisplay, {
                        attr: { cy: -100, r: 72 }, // Float high (-100), Large size (72)
                        duration: 1,
                        ease: 'elastic.out(1, 0.4)'
                    }, '-=0.6')
                    // Move text UP into the high bubble
                    .to(blobText, {
                        attr: { y: -98 }, // Centered in high bubble
                        duration: 1,
                        ease: 'elastic.out(1, 0.4)'
                    }, '<')
                    // Fade text in
                    .to(textGroup, {
                        opacity: 1,
                        duration: 0.3,
                        ease: 'power2.out'
                    }, '-=0.8')

                    // 2. Slide to Value (Elastic effect)
                    // We animate a proxy object and update the SVG elements
                    .to({ val: 0 }, {
                        val: maxVal,
                        duration: 2.5,
                        // Use a slightly more controlled ease to prevent extreme lag/overshoot mismatch
                        ease: 'elastic.out(1, 0.7)',
                        onUpdate: function () {
                            const currentVal = this.targets()[0].val;
                            const currentX = (currentVal / 100) * trackWidth;

                            // COMPENSATION: The bar has stroke-linecap: round, adding ~3px (half stroke width) 
                            // to the perceived end. We offset the balloon to match the visual tip of the bar.
                            const visualOffset = 2;

                            // Move the entire group (handle + bubble + text + connector)
                            gsap.set([blobGroup, textGroup], { x: currentX + visualOffset });

                            // Stretch the line
                            gsap.set(fillLine, { attr: { x2: 50 + currentX } });

                            // Update Text (Inside Blob)
                            if (blobText) {
                                blobText.textContent = currentVal.toFixed(1) + '%';
                            }
                        }
                    }, '-=0.5');

                // 3. Keep the blob UP (Speech Bubble state) as per user request
                // No "Blob Down" animation.
            }
        });
    }



    // Bottom Text
    const bottomText = section.querySelector('.inflation__bottom');
    if (bottomText) {
        gsap.from(bottomText, {
            scrollTrigger: {
                trigger: bottomText,
                start: 'top 85%',
            },
            y: 30,
            opacity: 0,
            duration: 1,
            ease: 'power3.out'
        });
    }

    // 4. Donut Charts Animation (Scroll-triggered)
    const donutContainers = section.querySelectorAll('.donut-container');

    if (donutContainers.length > 0) {
        donutContainers.forEach((container, index) => {
            const donutProgress = container.querySelector('.donut-progress');
            const countElement = container.querySelector('.count');
            const checkIcon = container.querySelector('.donut-check-icon');
            const speechBubble = container.querySelector('.speech-bubble');
            const numberWrap = container.querySelector('.donut-number-wrap');

            if (donutProgress && countElement) {
                const targetPct = parseFloat(donutProgress.getAttribute('data-pct'));
                const targetValue = parseFloat(countElement.getAttribute('data-target'));
                const isDecimal = targetValue % 1 !== 0;
                const circumference = 263.89;
                const offset = circumference - (targetPct / 100) * circumference;

                // 1. Initial State Setup (Hide elements to animate later)
                gsap.set(container, { opacity: 0, y: 50 }); // Container hidden
                gsap.set(donutProgress, { strokeDashoffset: circumference }); // Graph empty
                gsap.set(countElement, { innerText: 0 }); // Count 0

                if (numberWrap) {
                    gsap.set(numberWrap, { opacity: 0, scale: 0.5 }); // Hide number wrap initially
                }

                if (speechBubble) {
                    gsap.set(speechBubble, { scale: 0, opacity: 0, transformOrigin: 'center bottom' });
                }
                if (checkIcon) {
                    gsap.set(checkIcon, { scale: 0, opacity: 0, rotation: -30, transformOrigin: 'center center' });
                }

                // 2. Timeline Animation
                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: container,
                        start: 'top 85%',
                        once: true
                    },
                    delay: index * 0.2 // Stagger between cards
                });

                // Step 1: Container Fade In
                tl.to(container, {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    ease: 'power3.out'
                })

                // Step 2: Show Number (Pop up)
                if (numberWrap) {
                    tl.to(numberWrap, {
                        opacity: 1,
                        scale: 1,
                        duration: 0.5,
                        ease: 'back.out(1.7)'
                    }, '-=0.6');
                }

                // Step 3: Graph Fill & Counting (Concurrent)
                tl.to(donutProgress, {
                    strokeDashoffset: offset,
                    duration: 1.5,
                    ease: 'power2.out'
                }, '-=0.4') // Start filling right after number appears
                    .to(countElement, {
                        innerText: targetValue,
                        duration: 1.5,
                        ease: 'power2.out',
                        snap: isDecimal ? { innerText: 0.1 } : { innerText: 1 },
                        onUpdate: function () {
                            const currentValue = parseFloat(this.targets()[0].innerText);
                            countElement.innerText = isDecimal ? currentValue.toFixed(1) : Math.ceil(currentValue);
                        }
                    }, '<'); // Start with graph

                // Step 3: Speech Bubble Pop (After graph)
                if (speechBubble) {
                    tl.to(speechBubble, {
                        scale: 1,
                        opacity: 1,
                        duration: 0.6,
                        ease: 'back.out(1.7)'
                    }, '-=0.2'); // Slightly before graph ends
                }

                // Step 4: Check Icon Pop (Last)
                if (checkIcon) {
                    tl.to(checkIcon, {
                        scale: 1,
                        opacity: 1,
                        rotation: 0,
                        duration: 0.6,
                        ease: 'elastic.out(1, 0.5)'
                    });
                }
            }
        });
    }
};

// ------- Trust Section Animations ------- //
const initTrustAnimations = () => {
    const section = document.querySelector('.section--trust');
    if (!section) return;

    // 1. Text Effects (Blur Scroll)
    const title = section.querySelector('.trust__title');
    const desc = section.querySelector('.trust__desc');

    if (title) new BlurScrollEffect(title);
    if (desc) new BlurScrollEffect(desc);

    // 2. Subtitle (Simple Fade Up)
    const subtitle = section.querySelector('.trust__subtitle');
    if (subtitle) {
        gsap.from(subtitle, {
            scrollTrigger: {
                trigger: subtitle,
                start: 'top 90%',
            },
            y: 30,
            opacity: 0,
            duration: 1,
            ease: 'power3.out'
        });
    }

    // 3. Statistics Cards with Counting Animation
    const statsCards = section.querySelectorAll('.trust__stat-card');
    const statNumbers = section.querySelectorAll('.stat-number');

    if (statsCards.length > 0) {
        // Fade in cards
        gsap.from(statsCards, {
            scrollTrigger: {
                trigger: '.trust__stats-row',
                start: 'top 85%',
            },
            y: 20,
            opacity: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power3.out',
            clearProps: 'all'
        });

        // Count up numbers
        statNumbers.forEach(numberEl => {
            const target = parseFloat(numberEl.getAttribute('data-target'));
            const isDecimal = target % 1 !== 0; // Check if it has decimal places

            gsap.to(numberEl, {
                scrollTrigger: {
                    trigger: '.trust__stats-row',
                    start: 'top 85%',
                },
                innerText: target,
                duration: 2,
                ease: 'power2.out',
                snap: isDecimal ? { innerText: 0.1 } : { innerText: 1 }, // Snap to 0.1 for decimals, 1 for integers
                onUpdate: function () {
                    const currentValue = parseFloat(this.targets()[0].innerText);
                    if (isDecimal) {
                        numberEl.innerText = currentValue.toFixed(1);
                    } else {
                        numberEl.innerText = Math.ceil(currentValue);
                    }
                }
            });
        });
    }

    // 4. Certification Items (Fade In & Slide Up)
    const certRow = section.querySelector('.trust__cert-row');
    const certItems = section.querySelectorAll('.trust__cert-item');

    if (certItems.length > 0) {
        gsap.from(certItems, {
            scrollTrigger: {
                trigger: certRow || section,
                start: 'top 85%',
            },
            y: 30,
            opacity: 0,
            duration: 1,
            stagger: 0.2,
            ease: 'power3.out'
        });
    }

    // 5. Non-text Elements (Fade In & Float Up)
    const emblem = section.querySelector('.trust__emblem-wrap');
    const guarantee = section.querySelector('.trust__guarantee');
    const items = section.querySelectorAll('.trust__item');

    if (emblem) {
        gsap.from(emblem, {
            scrollTrigger: {
                trigger: emblem,
                start: 'top 85%',
            },
            y: 40,
            opacity: 0,
            duration: 1,
            delay: 0.2,
            ease: 'back.out(1.7)'
        });
    }

    if (guarantee) {
        gsap.from(guarantee, {
            scrollTrigger: {
                trigger: guarantee,
                start: 'top 85%',
            },
            y: 50,
            opacity: 0,
            duration: 1,
            ease: 'power3.out'
        });
    }

    /* 
    if (items.length > 0) {
        gsap.from(items, {
            scrollTrigger: {
                trigger: '.trust__grid',
                start: 'top 85%',
            },
            y: 40,
            opacity: 0,
            duration: 1,
            stagger: 0.1,
            ease: 'power3.out'
        });
    }
    */
};

// ------- Showcase Section Animations ------- //
const initShowcaseAnimations = () => {
    const section = document.querySelector('.section--showcase');
    if (!section) return;

    const title = section.querySelector('.showcase__title');
    const desc = section.querySelector('.showcase__desc');

    if (title) new BlurScrollEffect(title);
    if (desc) new BlurScrollEffect(desc);
};

// ------- Highlight Effect ------- //
const initHighlightEffect = () => {
    const els = document.querySelectorAll('.highlight-text');
    els.forEach(el => {
        // PriceHighlight 사용 (Highlight Marker)
        new PriceHighlight(el);
    });
};



// ------- Sticky Header Logic ------- //
const initStickyHeader = () => {
    const header = document.querySelector('.header');
    if (!header) return;

    ScrollTrigger.create({
        trigger: document.body,
        start: () => "top top-=" + (window.innerHeight - 100),
        toggleClass: { targets: header, className: "header--scrolled" }
    });
};

// ------- Osmo Draw Random Underline Logic ------- //
const svgVariants = [
    `<svg width="310" height="40" viewBox="0 0 310 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 20.9999C26.7762 16.2245 49.5532 11.5572 71.7979 14.6666C84.9553 16.5057 97.0392 21.8432 109.987 24.3888C116.413 25.6523 123.012 25.5143 129.042 22.6388C135.981 19.3303 142.586 15.1422 150.092 13.3333C156.799 11.7168 161.702 14.6225 167.887 16.8333C181.562 21.7212 194.975 22.6234 209.252 21.3888C224.678 20.0548 239.912 17.991 255.42 18.3055C272.027 18.6422 288.409 18.867 305 17.9999" stroke="currentColor" stroke-width="10" stroke-linecap="round"/></svg>`,
    `<svg width="310" height="40" viewBox="0 0 310 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 24.2592C26.233 20.2879 47.7083 16.9968 69.135 13.8421C98.0469 9.5853 128.407 4.02322 158.059 5.14674C172.583 5.69708 187.686 8.66104 201.598 11.9696C207.232 13.3093 215.437 14.9471 220.137 18.3619C224.401 21.4596 220.737 25.6575 217.184 27.6168C208.309 32.5097 197.199 34.281 186.698 34.8486C183.159 35.0399 147.197 36.2657 155.105 26.5837C158.11 22.9053 162.993 20.6229 167.764 18.7924C178.386 14.7164 190.115 12.1115 201.624 10.3984C218.367 7.90626 235.528 7.06127 252.521 7.49276C258.455 7.64343 264.389 7.92791 270.295 8.41825C280.321 9.25056 296 10.8932 305 13.0242" stroke="currentColor" stroke-width="10" stroke-linecap="round"/></svg>`,
    `<svg width="310" height="40" viewBox="0 0 310 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 29.5014C9.61174 24.4515 12.9521 17.9873 20.9532 17.5292C23.7742 17.3676 27.0987 17.7897 29.6575 19.0014C33.2644 20.7093 35.6481 24.0004 39.4178 25.5014C48.3911 29.0744 55.7503 25.7731 63.3048 21.0292C67.9902 18.0869 73.7668 16.1366 79.3721 17.8903C85.1682 19.7036 88.2173 26.2464 94.4121 27.2514C102.584 28.5771 107.023 25.5064 113.276 20.6125C119.927 15.4067 128.83 12.3333 137.249 15.0014C141.418 16.3225 143.116 18.7528 146.581 21.0014C149.621 22.9736 152.78 23.6197 156.284 24.2514C165.142 25.8479 172.315 17.5185 179.144 13.5014C184.459 10.3746 191.785 8.74853 195.868 14.5292C199.252 19.3205 205.597 22.9057 211.621 22.5014C215.553 22.2374 220.183 17.8356 222.979 15.5569C225.4 13.5845 227.457 11.1105 230.742 10.5292C232.718 10.1794 234.784 12.9691 236.164 14.0014C238.543 15.7801 240.717 18.4775 243.356 19.8903C249.488 23.1729 255.706 21.2551 261.079 18.0014C266.571 14.6754 270.439 11.5202 277.146 13.6125C280.725 14.7289 283.221 17.209 286.393 19.0014C292.321 22.3517 298.255 22.5014 305 22.5014" stroke="currentColor" stroke-width="10" stroke-linecap="round"/></svg>`
];

function initDrawRandomUnderline() {
    function decorateSVG(svgEl) {
        svgEl.setAttribute('class', 'text-draw__box-svg');
        svgEl.setAttribute('preserveAspectRatio', 'none');
        svgEl.querySelectorAll('path').forEach(path => {
            path.setAttribute('stroke', 'currentColor');
        });
    }

    let nextIndex = null;
    document.querySelectorAll('[data-draw-line]').forEach(container => {
        const box = container.querySelector('[data-draw-line-box]');
        if (!box) return;
        let enterTween = null;
        let leaveTween = null;

        container.addEventListener('mouseenter', () => {
            if (enterTween && enterTween.isActive()) return;
            if (leaveTween && leaveTween.isActive()) leaveTween.kill();

            if (nextIndex === null) {
                nextIndex = Math.floor(Math.random() * svgVariants.length);
            }

            box.innerHTML = svgVariants[nextIndex];
            const svg = box.querySelector('svg');
            if (svg) {
                decorateSVG(svg);
                const path = svg.querySelector('path');
                if (path) {
                    const length = path.getTotalLength();
                    gsap.set(path, {
                        strokeDasharray: length,
                        strokeDashoffset: length
                    });
                    enterTween = gsap.to(path, {
                        duration: 0.5,
                        strokeDashoffset: 0,
                        ease: 'power2.inOut',
                        onComplete: () => { enterTween = null; }
                    });
                }
            }
            nextIndex = (nextIndex + 1) % svgVariants.length;
        });

        container.addEventListener('mouseleave', () => {
            const path = box.querySelector('path');
            if (!path) return;

            const playOut = () => {
                if (leaveTween && leaveTween.isActive()) return;
                const length = path.getTotalLength();
                leaveTween = gsap.to(path, {
                    duration: 0.5,
                    strokeDashoffset: -length,
                    ease: 'power2.inOut',
                    onComplete: () => {
                        leaveTween = null;
                        box.innerHTML = '';
                    }
                });
            };

            if (enterTween && enterTween.isActive()) {
                enterTween.eventCallback('onComplete', playOut);
            } else {
                playOut();
            }
        });
    });
}

initDrawRandomUnderline();
// ------- Preparedness Stats Counter ------- //
const initPreparednessStats = () => {
    // Animate title
    gsap.from('.preparedness__title', {
        opacity: 0,
        y: 30,
        duration: 1,
        scrollTrigger: {
            trigger: '.preparedness__title',
            start: 'top bottom-=10%'
        }
    });

    // Animate subtitle
    gsap.from('.preparedness__subtitle', {
        opacity: 0,
        y: 20,
        duration: 1,
        delay: 0.2,
        scrollTrigger: {
            trigger: '.preparedness__subtitle',
            start: 'top bottom-=10%'
        }
    });

    const statCards = document.querySelectorAll('.prep-stat-content');

    statCards.forEach((card, index) => {
        const numberEl = card.querySelector('.prep-stat-content__number');
        const unitEl = card.querySelector('.prep-stat-content__unit');
        const target = parseFloat(numberEl.dataset.target);
        const hasDecimal = target % 1 !== 0;

        const counter = { value: 0 };

        gsap.to(counter, {
            value: target,
            duration: 2.5,
            ease: 'power2.out',
            delay: index * 0.15,
            scrollTrigger: {
                trigger: card,
                start: 'top bottom-=10%',
                toggleActions: 'play none none none'
            },
            onUpdate: function () {
                if (hasDecimal) {
                    numberEl.textContent = counter.value.toFixed(1);
                } else {
                    numberEl.textContent = Math.round(counter.value);
                }
            },
            onComplete: function () {
                if (unitEl) {
                    gsap.to(unitEl, {
                        opacity: 1,
                        y: 0,
                        duration: 0.5,
                        ease: 'back.out(1.2)'
                    });
                }
            }
        });

        gsap.from(card, {
            opacity: 0,
            y: 30,
            duration: 0.8,
            delay: index * 0.1,
            scrollTrigger: {
                trigger: card,
                start: 'top bottom-=5%'
            }
        });
    });

    // Animate bottom text
    gsap.from('.preparedness__bottom', {
        opacity: 0,
        y: 20,
        duration: 1,
        scrollTrigger: {
            trigger: '.preparedness__bottom',
            start: 'top bottom-=10%'
        }
    });
};


// ------- Difficulty Section Animations ------- //
const initDifficultyAnimations = () => {
    const section = document.querySelector('.section--difficulty');
    if (!section) return;

    // 1. Header Text
    const title = section.querySelector('.difficulty__title');
    const subtitle = section.querySelector('.difficulty__subtitle');

    if (title) {
        gsap.from(title, {
            scrollTrigger: {
                trigger: section,
                start: 'top 70%',
            },
            y: 50,
            opacity: 0,
            duration: 1,
            ease: 'power3.out'
        });
    }

    if (subtitle) {
        gsap.from(subtitle, {
            scrollTrigger: {
                trigger: section,
                start: 'top 75%',
            },
            y: 30,
            opacity: 0,
            duration: 1,
            delay: 0.2,
            ease: 'power3.out'
        });
    }

    // 2. Grid Items Stagger
    const items = section.querySelectorAll('.difficulty__item');
    /*
    if (items.length > 0) {
        gsap.from(items, {
            scrollTrigger: {
                trigger: '.difficulty__grid',
                start: 'top 80%',
            },
            y: 50,
            opacity: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power2.out'
        });
    }
    */

    // 3. Visual Specific Animations

    // Water Text Filling
    const waterTexts = section.querySelectorAll('.water-text');
    if (waterTexts.length > 0) {
        waterTexts.forEach(text => {
            // Set initial state
            text.style.setProperty('--water-p', '0%');

            gsap.to(text, {
                scrollTrigger: {
                    trigger: '.difficulty__grid',
                    start: 'top 70%',
                },
                '--water-p': '55%', // Animate to 55% (Half Full - Danger Level)
                duration: 2.0,
                ease: 'power2.inOut',
                stagger: 0.2
            });
        });
    }

    // Pie Chart
    const pieFill = section.querySelector('.pie-fill');
    if (pieFill) {
        const percent = 90;
        // Force initial state
        gsap.set(pieFill, { strokeDashoffset: 100 });

        gsap.to(pieFill, {
            scrollTrigger: {
                trigger: pieFill,
                start: 'top 85%',
            },
            strokeDashoffset: 100 - percent,
            duration: 1.5,
            ease: 'power2.out',
            delay: 0.5
        });
    }

    // Stress Heartbeat Animation
    const stressPath = section.querySelector('.stress-svg path');
    if (stressPath) {
        const length = stressPath.getTotalLength();
        // Set initial state: hidden line
        gsap.set(stressPath, { strokeDasharray: length, strokeDashoffset: length });

        gsap.to(stressPath, {
            scrollTrigger: {
                trigger: '.difficulty__grid',
                start: 'top 80%',
            },
            strokeDashoffset: 0,
            duration: 2.0,
            ease: "power2.out",
            delay: 0.5
        });
    }

    // Document Badge Pop
    const docBadge = section.querySelector('.doc-badge');
    if (docBadge) {
        gsap.from(docBadge, {
            scrollTrigger: {
                trigger: docBadge,
                start: 'top 85%',
            },
            scale: 0,
            opacity: 0,
            duration: 0.5,
            ease: 'back.out(1.7)',
            delay: 0.8
        });
    }
};


// Review Carousel (Based on User Snippet)
const initReviewCarousel = () => {
    const ring = document.querySelector('.review-ring');
    if (!ring) return;

    const reviewData = [
        { text: "<b>처음 겪는 일을 상조상품을 통해 진행하니•••</b> 신경 써야 할 일들이 줄어 마음이 조금 편안했습니다. 담당 지도사님과 직원분들이 친절하게 장례 진행을 도와주셔서 감사한 마음입니다. 감사드립니다.", date: "25년 12월 4일" },
        { text: "<b>그 마음과 손길이 감사했습니다•••</b> 밤늦게 어머님께서 돌아가셔서 더 경황이 없었는데 늦은 시간에도 친절하게 응대해 주시고 어머님 입관도 너무 곱고 예쁘게 잘 해주셔서 그 마음과 손길이 정말 감사했습니다.", date: "25년 10월 27일" },
        { text: "<b>순조로운 진행에••·</b> 장례지도사님께서 설명을 잘해주시고 잘 리드해 주셔서 장례 치르는데 많은 도움을 받고 순조로운 진행에 매우 감사를 드립니다.", date: "25년 11월 17일" },
        { text: "<b>주변에서도 업체 물어보며…</b> 납입 완료가 다 되어서 고민하던 시기에 장례를 치르게 되었습니다. 임종을 맞이하고 생각나던 건 상조상품. 전화 한 통에 장례식장에 미리 와 계시고...", date: "25년 12월 4일" },
        { text: "<b>세심한 배려에 감동받았습니다•••</b> 장례 준비부터 마무리까지 하나하나 세심하게 챙겨주셔서 유족으로서 큰 위안이 되었습니다. 어려운 시기에 든든한 지원이 되어주셔서 감사합니다.", date: "25년 9월 15일" },
        { text: "<b>전문적인 서비스에 만족합니다•••</b> 장례 절차에 대해 잘 몰랐는데, 처음부터 끝까지 친절하게 안내해 주시고 모든 것을 완벽하게 처리해 주셔서 정말 만족스러웠습니다.", date: "25년 8월 22일" },
        { text: "<b>믿고 맡길 수 있었습니다•••</b> 상조 서비스를 이용하길 잘했다는 생각이 듭니다. 모든 과정이 체계적이고 깔끔하게 진행되어 고인을 편안히 보낼 수 있었습니다.", date: "25년 11월 3일" },
        { text: "<b>가족처럼 대해주셔서•••</b> 슬픔에 잠긴 저희 가족을 진심으로 위로해 주시고, 마치 가족처럼 따뜻하게 대해주셔서 큰 힘이 되었습니다. 진심으로 감사드립니다.", date: "25년 10월 8일" },
        { text: "<b>빠른 대응이 인상적이었습니다•••</b> 급하게 연락드렸는데도 신속하게 출동해 주시고, 모든 준비를 완벽하게 해주셔서 정말 감사했습니다. 프로페셔널한 모습이 인상적이었습니다.", date: "25년 12월 11일" },
        { text: "<b>합리적인 비용에 만족합니다•••</b> 품질 높은 서비스를 합리적인 비용으로 받을 수 있어서 좋았습니다. 추가 비용 없이 약속된 서비스를 모두 제공받았습니다.", date: "25년 9월 28일" },
        { text: "<b>정성스러운 서비스에 감동•••</b> 고인을 정성스럽게 모셔주시고, 유족들의 마음까지 세심하게 배려해 주셔서 정말 감동받았습니다. 최고의 선택이었습니다.", date: "25년 11월 25일" },
        { text: "<b>안심하고 맡길 수 있었습니다•••</b> 처음부터 끝까지 투명하게 진행되어 안심하고 맡길 수 있었습니다. 장례지도사님의 전문성과 친절함에 깊은 감사를 드립니다.", date: "25년 10월 19일" }
    ];

    // Use all 12 unique reviews
    const reviews = reviewData;

    reviews.forEach((item, i) => {
        const card = document.createElement('div');
        card.classList.add('review-card');
        card.innerHTML = `
            <div class="review-card__icon"></div>
            <p class="review-card__text">"${item.text}"<br><small>${item.date}</small></p>
            <div class="review-card__logo"></div>
        `;
        ring.appendChild(card);
    });

    const cards = ring.querySelectorAll('.review-card');
    let xPos = 0;

    const updateCarousel = () => {
        const n = reviews.length;
        const cardWidth = ring.offsetWidth;
        // Calculate radius to make cards touch: Radius = (width/2) / tan(PI/n)
        // Adding a smaller multiplier (1.125) to reduce the gap by half of the previous setting
        const radius = Math.round(((cardWidth / 2) / Math.tan(Math.PI / n)) * 1.125);

        gsap.set(cards, {
            rotateY: (i) => i * - (360 / n),
            transformOrigin: `50% 50% ${radius}px`,
            z: -radius,
            backfaceVisibility: 'hidden'
        });

        // Set initial rotation
        gsap.set(ring, { rotationY: 180, cursor: 'grab' });
    };

    updateCarousel();
    window.addEventListener('resize', updateCarousel);

    gsap.timeline()
        .from(cards, {
            duration: 1.5,
            y: 200,
            opacity: 0,
            stagger: 0.1,
            ease: 'expo.out'
        });

    // Hover effects
    cards.forEach(card => {
        card.addEventListener('mouseenter', () => {
            gsap.to(cards, { opacity: (i, t) => (t === card) ? 1 : 0.5, ease: 'power3.out' });
        });
        card.addEventListener('mouseleave', () => {
            gsap.to(cards, { opacity: 1, ease: 'power2.inOut' });
        });
    });

    // Auto-rotation logic (Flipped direction as requested)
    let autoRotate = gsap.to(ring, {
        rotationY: "-=360",
        duration: 100, // Very slow rotation
        repeat: -1,
        ease: "none",
        paused: false
    });

    // Interaction logic
    let isUserInteracted = false;

    const dragStart = (e) => {
        isUserInteracted = true;
        autoRotate.kill(); // Permanently stop auto-rotation on interaction
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        xPos = Math.round(clientX);
        gsap.set(ring, { cursor: 'grabbing' });
        window.addEventListener('mousemove', drag);
        window.addEventListener('touchmove', drag);
    };

    const drag = (e) => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const delta = Math.round(clientX) - xPos;

        gsap.set(ring, {
            rotationY: `-=${delta * 0.5}`
        });

        xPos = Math.round(clientX);
    };

    const dragEnd = () => {
        window.removeEventListener('mousemove', drag);
        window.removeEventListener('touchmove', drag);
        gsap.set(ring, { cursor: 'grab' });
        // No longer resuming auto-rotation here
    };

    ring.addEventListener('mousedown', dragStart);
    ring.addEventListener('touchstart', dragStart, { passive: true });
    window.addEventListener('mouseup', dragEnd);
    window.addEventListener('touchend', dragEnd);

    // Nav Buttons Logic
    const nextBtn = document.querySelector('#reviews .review-btn--next');
    const prevBtn = document.querySelector('#reviews .review-btn--prev');

    if (nextBtn && prevBtn) {
        const step = 360 / reviews.length;

        nextBtn.addEventListener('click', () => {
            isUserInteracted = true;
            autoRotate.kill(); // Kill auto-rotation on button click
            gsap.to(ring, {
                rotationY: `-=${step}`,
                duration: 0.8,
                ease: "power2.out"
            });
        });

        prevBtn.addEventListener('click', () => {
            isUserInteracted = true;
            autoRotate.kill(); // Kill auto-rotation on button click
            gsap.to(ring, {
                rotationY: `+=${step}`,
                duration: 0.8,
                ease: "power2.out"
            });
        });
    }
};


// Specialty Section Entry Animations
const initSpecialtyAnimations = () => {
    // Award Icon
    gsap.from('.specialty__award-icon', {
        scrollTrigger: {
            trigger: '.specialty__award-icon',
            start: 'top 85%',
        },
        opacity: 0,
        y: 40,
        scale: 0.8,
        duration: 1.2,
        ease: 'power4.out'
    });

    // Main Title
    gsap.from('.specialty__main-title', {
        scrollTrigger: {
            trigger: '.specialty__main-title',
            start: 'top 85%',
        },
        opacity: 0,
        y: 50,
        duration: 1.2,
        ease: 'power3.out',
        delay: 0.2
    });

    // Specialty Cards - Staggered
    gsap.from('.specialty-card', {
        scrollTrigger: {
            trigger: '.specialty__grid',
            start: 'top 80%',
        },
        opacity: 0,
        y: 60,
        duration: 1,
        stagger: 0.2,
        ease: 'power2.out'
    });

    // Specialty Rows
    gsap.utils.toArray('.specialty-row').forEach((row, i) => {
        gsap.from(row, {
            scrollTrigger: {
                trigger: row,
                start: 'top 90%',
            },
            opacity: 0,
            y: 30,
            duration: 1,
            ease: 'power2.out'
        });
    });
};

// Network Section Scroll Animations
const initNetworkAnimations = () => {
    const section = document.querySelector('.section--network');
    if (!section) return;

    // Header Animation
    gsap.from('.network__header', {
        scrollTrigger: {
            trigger: '.network__header',
            start: 'top 85%',
        },
        opacity: 0,
        y: 40,
        duration: 1.2,
        ease: 'power3.out'
    });

    // Table & Map Content Animation
    gsap.from(['.network__table-wrapper', '.network__map-container'], {
        scrollTrigger: {
            trigger: '.network__content',
            start: 'top 80%',
        },
        opacity: 0,
        y: 60,
        duration: 1.2,
        stagger: 0.3,
        ease: 'power3.out'
    });
};

// Redundant block removed.

// Number Count Animation
const initCountAnimation = () => {
    const countElements = document.querySelectorAll('.js-count-up');

    const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const element = entry.target;
                const target = parseInt(element.getAttribute('data-target'));
                const duration = 2000; // 2초 동안 진행
                const frameDuration = 1000 / 60;
                const totalFrames = Math.round(duration / frameDuration);
                const easeOutQuad = t => t * (2 - t);
                let frame = 0;

                const counter = setInterval(() => {
                    frame++;
                    const progress = easeOutQuad(frame / totalFrames);
                    const currentCount = Math.round(target * progress);

                    if (parseInt(element.innerText.replace(/,/g, '')) !== currentCount) {
                        element.innerText = currentCount.toLocaleString();
                    }

                    if (frame === totalFrames) {
                        clearInterval(counter);
                        element.innerText = target.toLocaleString();
                    }
                }, frameDuration);

                observer.unobserve(element);
            }
        });
    }, { threshold: 0.5 });

    countElements.forEach(el => observer.observe(el));
};

// Feature Icon Animations (Animated Transforms)
const initFeatureIconAnimations = () => {
    // Select product cards, quality icon items, and app system visual only (removed report)
    const cards = document.querySelectorAll('.product__feature-card, .q-icon-item, .system-item__visual--app');
    if (!cards.length) return;

    // 모든 카드에 공통적으로 좌우 흔들림(Wobble) 애니메이션 적용
    cards.forEach((card, i) => {
        const icon = card.querySelector('.feature-icon');
        if (!icon) return;

        // 약간의 시차(delay)를 주어 자연스럽게 연출
        gsap.to(icon, {
            '--icon-rot': '5deg', // 살짝만 흔들리도록 각도 조정 (너무 크면 어지러움)
            duration: 2 + (i * 0.1), // 카드마다 속도를 아주 미세하게 다르게
            yoyo: true,
            repeat: -1,
            ease: 'power1.inOut',
            delay: i * 0.2 // 순차적 시작
        });

        // 추가: 둥실둥실 부유 효과도 살짝 섞어서 더 입체적으로
        gsap.to(icon, {
            '--icon-y': '-10px',
            duration: 3 + (i * 0.2),
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut',
            delay: i * 0.3
        });
    });
};

// Timeline Animation
// Timeline Animation (Click Interactive)
// Timeline Animation
const initTimelineAnimation = () => {
    const timeline = document.querySelector('.product__timeline-wrap');
    if (!timeline) return;

    const steps = document.querySelectorAll('.timeline-step');
    const progressBar = document.querySelector('.timeline-progress');
    const totalSteps = steps.length;
    const bar = document.querySelector('.timeline-bar');

    let mm = gsap.matchMedia();

    mm.add("(max-width: 768px)", () => {
        // --- Mobile Logic (ScrollTrigger) ---

        // Initial State for Mobile
        gsap.set(progressBar, { width: '100%', height: '0%' });

        // Remove active class from all first
        steps.forEach(s => {
            s.classList.remove('active');
            gsap.set(s.querySelector('.step-content'), { opacity: 0.5, y: 0 });
        });

        const st = ScrollTrigger.create({
            trigger: timeline,
            start: "top 70%",
            end: "bottom 70%",
            scrub: 1, // 부드러운 스크러빙
            onUpdate: (self) => {
                const progress = self.progress * 100;

                // 세로 바 채우기
                gsap.to(progressBar, {
                    height: `${progress}%`,
                    duration: 0.1,
                    overwrite: true,
                    ease: "none"
                });

                // 단계별 활성화
                steps.forEach((step, index) => {
                    // 각 단계가 켜질 임계값 계산 (0, 0.33, 0.66, 1.0)
                    // 조금 더 일찍 켜지도록 -0.1 보정
                    const threshold = (index / (totalSteps - 1)) - 0.1;

                    if (self.progress >= threshold) {
                        if (!step.classList.contains('active')) {
                            step.classList.add('active');
                            gsap.to(step.querySelector('.step-content'), {
                                opacity: 1,
                                y: 0,
                                duration: 0.4,
                                overwrite: true
                            });
                            // 점 효과 강조
                            gsap.to(step.querySelector('.step-dot'), {
                                scale: 1.2,
                                borderColor: '#C5A065',
                                boxShadow: '0 0 15px rgba(197, 160, 101, 0.6)',
                                duration: 0.3
                            });
                        }
                    } else {
                        if (step.classList.contains('active')) {
                            step.classList.remove('active');
                            gsap.to(step.querySelector('.step-content'), {
                                opacity: 0.5,
                                y: 0,
                                duration: 0.4,
                                overwrite: true
                            });
                            // 점 효과 복구
                            gsap.to(step.querySelector('.step-dot'), {
                                scale: 1,
                                borderColor: '#C5A065',
                                boxShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
                                duration: 0.3
                            });
                        }
                    }
                });
            }
        });

        return () => {
            // cleanup if needed when switching to desktop
            if (st) st.kill();
        };
    });

    mm.add("(min-width: 769px)", () => {
        // --- Desktop Logic (Click/Hover) ---

        // Initial State for Desktop
        gsap.set(progressBar, { height: '100%', width: '0%' });

        const updateDesktopProgress = (targetIndex) => {
            const targetStep = steps[targetIndex];
            const dot = targetStep.querySelector('.step-dot');

            if (dot && bar) {
                const dotCenter = targetStep.offsetLeft + dot.offsetLeft + (dot.offsetWidth / 2);
                const barStart = bar.offsetLeft;
                const barWidth = bar.offsetWidth;
                let progressWidth = dotCenter - barStart;
                let progressPercent = (progressWidth / barWidth) * 100;

                if (targetIndex === steps.length - 1) progressPercent = 100;
                progressPercent = Math.max(0, Math.min(100, progressPercent));

                gsap.to(progressBar, {
                    width: `${progressPercent}%`,
                    duration: 0.6,
                    ease: 'power2.out'
                });
            }

            steps.forEach((step, i) => {
                const content = step.querySelector('.step-content');
                if (i <= targetIndex) {
                    step.classList.add('active');
                    gsap.to(content, { opacity: 1, y: 0, duration: 0.4, delay: i * 0.1, overwrite: true });
                } else {
                    step.classList.remove('active');
                    gsap.to(content, { opacity: 0.4, y: 0, duration: 0.4, overwrite: true });
                }
            });
        };

        // Event Listeners
        steps.forEach((step, index) => {
            step.addEventListener('mouseenter', () => updateDesktopProgress(index));
            step.addEventListener('click', () => updateDesktopProgress(index));
        });

        // Initialize
        updateDesktopProgress(0);

        return () => {
            // cleanup listeners is hard with anonymous functions, 
            // but matchMedia handles basic state cleanup usually.
            // GSAP cleans up animations on context revert.
            // We might want to remove listeners if we want strict cleanup but for this simple case it's okay.
        };
    });
};
const warrantyData = {
    happy450: {
        title: "더해피 450 상품 상세 구성",
        content: `
            <table class="product-detail-table-premium">
                <thead>
                    <tr>
                        <th colspan="3">구분</th>
                        <th style="width: 45%;">의전 구성 (상세)</th>
                    </tr>
                </thead>
                <tbody>
                    <!-- 1. 고인용품 및 수시용품 -->
                    <tr>
                        <td rowspan="6" class="main-cat">고인용품<br>및<br>수시용품</td>
                        <td rowspan="2" class="sub-cat">관</td>
                        <td class="third-cat">매장 시</td>
                        <td>오동나무 45mm</td>
                    </tr>
                    <tr>
                        <td class="third-cat">화장 또는 탈관 시</td>
                        <td>오동나무 18mm, 횡대 or 유골함 中 택1</td>
                    </tr>
                    <tr>
                        <td rowspan="2" class="sub-cat">수의</td>
                        <td class="third-cat">수의</td>
                        <td>대마 (대마100%)</td>
                    </tr>
                    <tr>
                        <td class="third-cat">수의 준비 고객</td>
                        <td>꽃관보 또는 장례의전 도우미 1명</td>
                    </tr>
                    <tr>
                        <td rowspan="2" class="sub-cat">입관 및<br>수시용품</td>
                        <td class="third-cat">도포, 원삼, 천금, 지금</td>
                        <td>수의 제품과 동일제품 제공</td>
                    </tr>
                    <tr>
                        <td class="third-cat">명정, 관보, 습신, 한지 등</td>
                        <td>규격품 제공</td>
                    </tr>

                    <!-- 2. 빈소 및 기타용품 -->
                    <tr>
                        <td rowspan="3" class="main-cat">빈소 및<br>기타용품</td>
                        <td class="sub-cat">빈소용품</td>
                        <td class="third-cat">향, 초, 부의록, 축문, 위패</td>
                        <td>필요량 일체 제공</td>
                    </tr>
                    <tr>
                        <td class="sub-cat">대여용품</td>
                        <td class="third-cat">향로, 촛대, 잔대</td>
                        <td>대여품</td>
                    </tr>
                    <tr>
                        <td class="sub-cat">기타용품</td>
                        <td class="third-cat">완장, 상장, 운구용장갑</td>
                        <td>필요량 일체 제공</td>
                    </tr>

                    <!-- 3. 의전용품 -->
                    <tr>
                        <td rowspan="3" class="main-cat">의전용품</td>
                        <td class="sub-cat">현대식</td>
                        <td class="third-cat">남성 / 여성</td>
                        <td>5벌 제공</td>
                    </tr>
                    <tr>
                        <td rowspan="2" class="sub-cat">전통식</td>
                        <td class="third-cat">바지저고리, 삼베치마 등</td>
                        <td>필요량 일체 제공</td>
                    </tr>
                    <tr>
                        <td class="third-cat">두루마기, 치마저고리 등</td>
                        <td>필요량 일체 제공</td>
                    </tr>

                    <!-- 4. 제단장식 -->
                    <tr>
                        <td rowspan="3" class="main-cat">제단장식<br>(꽃장식)</td>
                        <td colspan="2" class="sub-cat">생화꽃 제단장식</td>
                        <td>제단 꽃장식 35만원 상당</td>
                    </tr>
                    <tr>
                        <td colspan="2" class="sub-cat">헌화용 국화</td>
                        <td>30송이 제공</td>
                    </tr>
                    <tr>
                        <td colspan="2" class="sub-cat">꽃바구니</td>
                        <td>꽃바구니 2개</td>
                    </tr>

                    <!-- 5. 차량지원 -->
                    <tr>
                        <td rowspan="3" class="main-cat">차량지원</td>
                        <td colspan="2" class="sub-cat">운구 이송차량</td>
                        <td>관내 (시, 군내) 제공</td>
                    </tr>
                    <tr>
                        <td colspan="2" class="sub-cat">고인 리무진</td>
                        <td>왕복 300km (초과시 2,000원/ km)</td>
                    </tr>
                    <tr>
                        <td colspan="2" class="sub-cat">유족 버스</td>
                        <td>왕복 300km (초과시 2,000원/ km)</td>
                    </tr>

                    <!-- 6. 인력지원 -->
                    <tr>
                        <td rowspan="2" class="main-cat">인력지원</td>
                        <td colspan="2" class="sub-cat">장례지도사</td>
                        <td>1명 (입관지원 1명)</td>
                    </tr>
                    <tr>
                        <td colspan="2" class="sub-cat">장례의전 도우미<br>(1일/10시간)</td>
                        <td>5명 (1인 10시간)</td>
                    </tr>

                    <!-- 7. 편의서비스 -->
                    <tr>
                        <td rowspan="2" class="main-cat">편의서비스</td>
                        <td colspan="2" class="sub-cat">일회용식기세트</td>
                        <td>300 인분</td>
                    </tr>
                    <tr>
                        <td colspan="2" class="sub-cat">편의용품 (6인)</td>
                        <td>1세트 (6인)</td>
                    </tr>
                </tbody>
            </table>
        `
    },
    specialty_1: {
        title: "처음부터 끝까지",
        content: `
            <p class="modal-main-text">장례지도사는 1건의 장례가 끝날 때까지 책임지고 함께합니다.</p>
            <p class="modal-sub-text">24시간 긴급의전센터(1588-2227)를 운영하며 접수 시 전문 장례지도사가 2시간 이내 현장에 도착하여 도와드립니다.</p>
            <p class="modal-footnote">*도서 및 산간지역 제외. 상황에 따라 출동시간은 변동될 수 있음</p>
        `
    },
    specialty_2: {
        title: "전문가와 같이",
        content: `
            <p class="modal-main-text">고객 만족도 99%*의 전문 장례지도사가 장례물품 준비부터 장례 진행, 행정 절차까지 유가족이 큰 어려움 없이 마무리할 수 있도록 곁에서 세심하게 관리해 드립니다.</p>
            <p class="modal-footnote">*2023년 기준</p>
        `
    },
    specialty_3: {
        title: "용품 보증 시스템",
        content: `
            <p class="modal-main-text">규격용품보다 하위용품은 사용하지 않습니다.</p>
            <p class="modal-sub-text">소노아임레디만의 디자인 특허 고깔, 대마 100% 수의 등 빠짐없이 정직하게 준비해 드립니다.</p>
            <p class="modal-footnote">*고깔: 디자인등록증 제 30-1110105호 / 수의 : 2024년 1월 fiti 직물테스트 기준 1년 단위 주기 테스트 진행</p>
        `
    },
    supplies: {
        title: '고인용품 및 수시용품',
        content: `
            <table class="warranty-table">
                <tbody>
                    <tr>
                        <th rowspan="4">고인용품<br>및<br>수시용품</th>
                        <th class="warranty-sub-th">관</th>
                        <td>매장 시: 오동나무 45mm<br>화장 또는 탈관 시: 오동나무 18mm, 횡대 or 유골함 中 택1</td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th">수의</th>
                        <td>대마 (대마 100%)<br><span style="font-size:0.9em; color:#666;">- 수의 준비 고객: 꽃관보 또는 장례의전 도우미 1명 대체</span></td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th" rowspan="2">입관<br>및<br>수시용품</th>
                        <td>도포, 원삼, 천금, 지금 등<br>(수의 제품과 동일제품 제공)</td>
                    </tr>
                     <tr>
                        <td>명정, 관보, 습신, 한지 등<br>규격품 제공</td>
                    </tr>
                </tbody>
            </table>
        `
    },
    mortuary: {
        title: '빈소 및 기타용품',
        content: `
            <table class="warranty-table">
                <tbody>
                    <tr>
                        <th rowspan="3">빈소<br>및<br>기타용품</th>
                        <th class="warranty-sub-th">빈소용품</th>
                        <td>향, 초, 부의록, 축문, 위패 (필요량 일체 제공)</td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th">대여용품</th>
                        <td>향로, 촛대, 잔대 (대여품)</td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th">기타용품</th>
                        <td>완장, 상장, 운구용장갑 (필요량 일체 제공)</td>
                    </tr>
                </tbody>
            </table>
        `
    },
    clothing: {
        title: '의전용품 (상복)',
        content: `
            <table class="warranty-table">
                <tbody>
                    <tr>
                        <th rowspan="3">의전용품</th>
                        <th class="warranty-sub-th">현대식</th>
                        <td>남성 / 여성 (총 5벌 제공)</td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th" rowspan="2">전통식</th>
                        <td>바지저고리, 삼베치마 등 (필요량 일체 제공)</td>
                    </tr>
                     <tr>
                        <td>두루마기, 치마저고리 등 (필요량 일체 제공)</td>
                    </tr>
                </tbody>
            </table>
        `
    },
    altar: {
        title: '제단장식 (꽃장식)',
        content: `
            <table class="warranty-table">
                <tbody>
                    <tr>
                        <th rowspan="3">제단장식<br>(꽃장식)</th>
                        <th class="warranty-sub-th">생화꽃<br>제단장식</th>
                        <td>제단 꽃장식 35만원 상당 제공</td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th">헌화용 국화</th>
                        <td>30송이 제공</td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th">꽃바구니</th>
                        <td>꽃바구니 2개 제공</td>
                    </tr>
                </tbody>
            </table>
        `
    },
    vehicle: {
        title: '차량지원',
        content: `
            <table class="warranty-table">
                <tbody>
                    <tr>
                        <th rowspan="3">차량지원</th>
                        <th class="warranty-sub-th">운구<br>이송차량</th>
                        <td>관내 (시, 군내) 무료 제공</td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th">고인 리무진</th>
                        <td>왕복 300km (초과 시 2,000원/km)</td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th">유족 버스</th>
                        <td>왕복 300km (초과 시 2,000원/km)</td>
                    </tr>
                </tbody>
            </table>
        `
    },
    manpower: {
        title: '인력지원',
        content: `
            <table class="warranty-table">
                <tbody>
                    <tr>
                        <th rowspan="2">인력지원</th>
                        <th class="warranty-sub-th">장례지도사</th>
                        <td>1명 (입관지원 포함)</td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th">장례의전<br>도우미</th>
                        <td>5명 (1인 10시간 기준)<br><span style="font-size:0.9em; color:#666;">(1일/10시간 기준)</span></td>
                    </tr>
                </tbody>
            </table>
        `
    },
    convenience: {
        title: '편의서비스',
        content: `
            <table class="warranty-table">
                <tbody>
                    <tr>
                        <th rowspan="2">편의서비스</th>
                        <th class="warranty-sub-th">일회용<br>식기세트</th>
                        <td>300인분 제공</td>
                    </tr>
                    <tr>
                        <th class="warranty-sub-th">편의용품</th>
                        <td>1세트 (6인 기준) 제공</td>
                    </tr>
                </tbody>
            </table>
        `
    }
};

const initWarrantySystem = () => {
    // Select warranty items, specialty buttons, and product more button
    const items = document.querySelectorAll('.warranty-item, .specialty-premium-btn, .product__more-btn');
    const modal = document.getElementById('warrantyModal');
    if (!modal) return;

    const modalTitle = modal.querySelector('.warranty-modal__title');
    const modalBody = modal.querySelector('.warranty-modal__body');
    const closeBtn = modal.querySelector('.warranty-modal__close');
    const overlay = modal.querySelector('.warranty-modal__overlay');

    const closeModal = () => {
        modal.classList.remove('is-open');
        document.body.style.overflow = ''; // 스크롤 복구
    };

    items.forEach(item => {
        item.addEventListener('click', () => {
            const targetId = item.getAttribute('data-modal-target');
            const data = warrantyData[targetId];

            if (data) {
                modalTitle.textContent = data.title;
                modalBody.innerHTML = data.content;
                modal.classList.add('is-open');
                document.body.style.overflow = 'hidden'; // 배경 스크롤 방지
            }
        });
    });

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (overlay) overlay.addEventListener('click', closeModal);

    // ESC 키로 닫기
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) {
            closeModal();
        }
    });
};

// Membership Slider
const initMembershipSlider = () => {
    const track = document.querySelector('.membership__track');
    const prevBtn = document.querySelector('.membership-prev');
    const nextBtn = document.querySelector('.membership-next');

    if (!track || !prevBtn || !nextBtn) return;

    const scrollAmount = 300; // 이동 거리

    prevBtn.addEventListener('click', () => {
        track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', () => {
        track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });
};

// Benefit Tabs Switching logic
const initBenefitTabs = () => {
    const tabs = document.querySelectorAll('.benefit-tab');
    const panes = document.querySelectorAll('.tab-pane');
    const membershipId = 'membership';
    const hybridId = 'hybrid';

    const switchTab = (target) => {
        tabs.forEach(t => {
            if (t.getAttribute('data-tab') === target) {
                t.classList.add('active');
            } else {
                t.classList.remove('active');
            }
        });

        panes.forEach(p => {
            if (p.id === `pane-${target}`) {
                p.classList.add('active');
            } else {
                p.classList.remove('active');
            }
        });

        // Trigger a custom event or refresh slider if needed
        window.dispatchEvent(new Event('resize'));
    };

    tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            const target = tab.getAttribute('data-tab');
            switchTab(target);

            // Update URL without jump if possible, or just let it be
            // history.replaceState(null, null, `#${target}`);
        });
    });

    // Handle initial hash or clicks from nav
    const handleHash = () => {
        const hash = window.location.hash.replace('#', '');
        if (hash === hybridId) {
            switchTab('hybrid');
        } else if (hash === membershipId) {
            switchTab('membership');
        }
    };

    window.addEventListener('hashchange', handleHash);
    handleHash(); // Run on load
};

// FAQ Accordion
const initFAQ = () => {
    const questions = document.querySelectorAll('.faq__question');

    questions.forEach(q => {
        q.addEventListener('click', () => {
            const item = q.parentElement;
            const isActive = item.classList.contains('active');

            // Close all others
            document.querySelectorAll('.faq__item').forEach(i => {
                i.classList.remove('active');
                i.querySelector('.faq__answer').style.height = 0;
            });

            // Toggle current
            if (!isActive) {
                item.classList.add('active');
                const answer = item.querySelector('.faq__answer');
                answer.style.height = answer.scrollHeight + 'px';
            }
        });
    });
};

// Floating Button Visibility Control (Hide when reaching Inquiry Section)
const initFloatingVisibility = () => {
    const pin = document.querySelector('.pin');
    const floatLeft = document.querySelector('.floating-left');
    const inquirySection = document.querySelector('.section--inquiry');

    if (!pin || !inquirySection) return;

    ScrollTrigger.create({
        trigger: inquirySection,
        start: 'top bottom-=100', // When inquiry section is near bottom of viewport
        onEnter: () => {
            gsap.to([pin, floatLeft], { autoAlpha: 0, scale: 0.8, duration: 0.3 });
        },
        onLeaveBack: () => {
            gsap.to([pin, floatLeft], { autoAlpha: 1, scale: 1, duration: 0.3 });
        }
    });
};

// Scroll Animations for FAQ & Inquiry
const initBottomAnimations = () => {
    // 1. FAQ Title
    const faqTitle = document.querySelector('.faq__title');
    if (faqTitle) {
        gsap.from(faqTitle, {
            scrollTrigger: {
                trigger: '.section--faq',
                start: 'top 80%',
            },
            y: 50,
            opacity: 0,
            duration: 1,
            ease: 'power3.out'
        });
    }

    // 2. FAQ Items (Staggered)
    const faqItems = document.querySelectorAll('.faq__item');
    if (faqItems.length > 0) {
        gsap.from(faqItems, {
            scrollTrigger: {
                trigger: '.faq__list',
                start: 'top 85%',
            },
            y: 40,
            opacity: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power2.out'
        });
    }

    // 3. Inquiry Card (Pop up)
    const inquiryCard = document.querySelector('.inquiry__card');
    if (inquiryCard) {
        gsap.from(inquiryCard, {
            scrollTrigger: {
                trigger: '.section--inquiry',
                start: 'top 75%',
            },
            y: 80,
            opacity: 0,
            duration: 1.2,
            ease: 'power3.out'
        });
    }
};

// Privacy Policy Toggle
const initPrivacyToggle = () => {
    // Footer privacy toggle
    const link = document.querySelector('.inquiry__agreement-link:not(#toggle-privacy-modal)');
    const box = document.querySelector('.inquiry__privacy-box:not(#privacy-box-modal)');

    if (link && box) {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            const isHidden = box.style.display === 'none';
            box.style.display = isHidden ? 'block' : 'none';
            link.textContent = isHidden ? '닫기' : '자세히';
        });
    }

    // Modal privacy toggle
    const modalLink = document.getElementById('toggle-privacy-modal');
    const modalBox = document.getElementById('privacy-box-modal');

    if (modalLink && modalBox) {
        modalLink.addEventListener('click', (e) => {
            e.preventDefault();
            const isHidden = modalBox.style.display === 'none';
            modalBox.style.display = isHidden ? 'block' : 'none';
            modalLink.textContent = isHidden ? '닫기' : '자세히';
        });
    }
};

// Modal Close Button Handler - imported from modal-close-handler.js

// Footer Modals Logic
const initFooterModals = () => {
    console.log('initFooterModals called');

    const modals = {
        'btn-privacy': 'modal-privacy',
        'btn-privacy-floating': 'modal-privacy',
        'btn-privacy-inquiry': 'modal-privacy',
        'btn-notice': 'modal-notice',
        'btn-consult-modal-1': 'modal-consultation',
        'btn-consult-modal-2': 'modal-consultation',
        'btn-consult-modal-3': 'modal-consultation',
        'btn-consult-modal-essence': 'modal-consultation'
    };

    Object.keys(modals).forEach(btnId => {
        const btn = document.getElementById(btnId);
        const modalId = modals[btnId];
        const modal = document.getElementById(modalId);

        console.log(`Checking ${btnId}: btn=${!!btn}, modal=${!!modal}`);

        if (btn && modal) {
            console.log(`Setting up event listener for ${btnId}`);

            // Open Modal
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log(`Button clicked: ${btnId}, opening modal: ${modalId}`);

                modal.style.display = 'flex';
                // Small delay for simple CSS transition if we want fade in
                setTimeout(() => {
                    modal.classList.add('is-open');
                    console.log(`Modal ${modalId} should now be visible`);
                }, 10);
                document.body.style.overflow = 'hidden'; // Prevent background scrolling
                if (typeof lenis !== 'undefined' && lenis) lenis.stop();
            });

            // Close Modal (Button & Overlay & ESC)
            const closeBtn = modal.querySelector('.modal__close');
            const overlay = modal.querySelector('.modal__overlay');

            const closeModal = () => {
                console.log(`Closing modal: ${modalId}`);
                modal.classList.remove('is-open');
                setTimeout(() => {
                    modal.style.display = 'none';
                }, 300); // Wait for transition
                document.body.style.overflow = '';
                if (typeof lenis !== 'undefined' && lenis) lenis.start();
            };

            if (closeBtn) {
                closeBtn.addEventListener('click', closeModal);
                console.log(`Close button found for ${modalId}`);
            } else {
                console.warn(`No close button found for ${modalId}`);
            }

            if (overlay) {
                overlay.addEventListener('click', closeModal);
                console.log(`Overlay found for ${modalId}`);
            } else {
                console.warn(`No overlay found for ${modalId}`);
            }

            // ESC Key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && modal.classList.contains('is-open')) {
                    closeModal();
                }
            });
        } else {
            console.error(`Missing element for ${btnId}: btn=${!!btn}, modal=${!!modal}`);
        }
    });

    console.log('initFooterModals completed');

    // DEBUG: Check what element is actually receiving clicks
    setTimeout(() => {
        const btn = document.getElementById('btn-consult-modal-1');
        if (btn) {
            const rect = btn.getBoundingClientRect();
            const btnCenterX = rect.left + rect.width / 2;
            const btnCenterY = rect.top + rect.height / 2;
            const elementAtButton = document.elementFromPoint(btnCenterX, btnCenterY);

            console.log('=== BUTTON CLICK DEBUG ===');
            console.log('Button element:', btn);
            console.log('Element at button position:', elementAtButton);
            console.log('Element tag:', elementAtButton?.tagName);
            console.log('Element class:', elementAtButton?.className);
            console.log('Element id:', elementAtButton?.id);
            console.log('Is same element?', elementAtButton === btn);
            console.log('Button computed styles:', {
                pointerEvents: window.getComputedStyle(btn).pointerEvents,
                zIndex: window.getComputedStyle(btn).zIndex,
                position: window.getComputedStyle(btn).position
            });
            if (elementAtButton && elementAtButton !== btn) {
                console.log('BLOCKER computed styles:', {
                    pointerEvents: window.getComputedStyle(elementAtButton).pointerEvents,
                    zIndex: window.getComputedStyle(elementAtButton).zIndex,
                    position: window.getComputedStyle(elementAtButton).position
                });
            }
        }
    }, 2000);
};

// YouTube Section Animations
const initYoutubeAnimations = () => {
    // Header sequence
    gsap.from('.youtube__deco-text', {
        scrollTrigger: {
            trigger: '.section--youtube',
            start: 'top 85%',
        },
        opacity: 0,
        scale: 0.9,
        duration: 2.5,
        ease: 'power3.out'
    });

    gsap.from(".youtube__icon-3d", {
        scrollTrigger: {
            trigger: ".section--youtube",
            start: "top 70%",
        },
        opacity: 0,
        y: 50,
        scale: 0.2,
        rotation: -45,
        duration: 1.5,
        ease: "back.out(1.7)"
    });

    gsap.from('.youtube__tag', {
        scrollTrigger: {
            trigger: '.section--youtube',
            start: 'top 80%',
        },
        opacity: 0,
        y: 20,
        duration: 0.8,
        ease: 'power2.out'
    });

    gsap.from('.youtube__title', {
        scrollTrigger: {
            trigger: '.section--youtube',
            start: 'top 75%',
        },
        opacity: 0,
        y: 30,
        duration: 1,
        delay: 0.2,
        ease: 'power3.out'
    });

    gsap.from('.youtube__desc', {
        scrollTrigger: {
            trigger: '.section--youtube',
            start: 'top 70%',
        },
        opacity: 0,
        y: 30,
        duration: 1,
        delay: 0.4,
        ease: 'power3.out'
    });

    // Video Wrapper
    gsap.from('.youtube__video-wrapper', {
        scrollTrigger: {
            trigger: '.youtube__video-wrapper',
            start: 'top 80%',
        },
        opacity: 0,
        scale: 0.95,
        y: 50,
        duration: 1.2,
        ease: 'power4.out'
    });

    // CTA Box
    gsap.from('.youtube__cta-box', {
        scrollTrigger: {
            trigger: '.youtube__cta-box',
            start: 'top 90%',
        },
        opacity: 0,
        y: 30,
        duration: 1,
        ease: 'power3.out'
    });
};

// ------- Trust Section Lighting Animation ------- //
const initTrustLightingAnimation = () => {
    const section = document.querySelector('#trust');
    const overlay = section?.querySelector('.light-up-overlay');
    const container = section?.querySelector('.trust__container');
    
    if(!section || !overlay || !container) return;

    // Lights go on (Overlay disappears)
    gsap.to(overlay, {
        opacity: 0,
        scrollTrigger: {
            trigger: section,
            start: "top 60%", 
            end: "top 10%",
            scrub: 1.2,
        }
    });

    // Content reveals
    gsap.set(container, { opacity: 0, y: 30 }); // Initial state
    gsap.to(container, {
        opacity: 1,
        y: 0,
        scrollTrigger: {
            trigger: section,
            start: "top 55%",
            end: "top 15%",
            scrub: 1.0,
        }
    });
};

// Background Color Transition Logic
const initColorTransition = () => {
    // 0. Ensure initial state is clearly defined via set if needed, but CSS handles var
    gsap.set("body", { "--bg-color": "#000000" });

    // 1. Showcase -> Trust (Black to White)
    gsap.to("body", {
        "--bg-color": "#ffffff", // Pure white for trust section
        immediateRender: false,
        scrollTrigger: {
            trigger: "#trust",
            start: "top 60%", // Starts when section is 40% into the viewport
            end: "top 10%",      
            scrub: true
        }
    });

    // 2. Trust -> Inflation (White to Black)
    gsap.to("body", {
        "--bg-color": "#000000", // Target Color: Black
        immediateRender: false,
        scrollTrigger: {
            trigger: ".section--inflation",
            start: "top bottom", // Starts when top of Inflation hits bottom of viewport
            end: "top top",      // Ends when top of Inflation hits top of viewport
            scrub: true
        }
    });
};

// Reset color on page refresh to handle potential glitches if starting in middle
ScrollTrigger.addEventListener("refresh", () => {
    // Optional: Force check but GSAP usually handles this
});

// Hero Video Popup Logic
let isVideoPopupInitialized = false;
const initHeroVideoPopup = () => {
    if (isVideoPopupInitialized) return;

    const popup = document.getElementById('hero-video-popup');
    const overlay = document.querySelector('.video-popup-overlay');
    const closeBtn = document.querySelector('.video-popup__close-expanded');
    const dismissBtn = document.querySelector('.video-popup__close-individual');
    const soundBtn = document.querySelector('.video-popup__sound-toggle');
    const video = popup?.querySelector('video');

    if (!popup || !overlay) return;
    isVideoPopupInitialized = true;

    // Make popup draggable (only when not expanded)
    let draggableInstance = Draggable.create(popup, {
        type: 'x,y',
        bounds: 'body',
        edgeResistance: 0.65,
        cursor: 'grab',
        activeCursor: 'grabbing',
        onDragStart: function () {
            popup.style.cursor = 'grabbing';
        },
        onDragEnd: function () {
            popup.style.cursor = 'grab';
        },
        onClick: function (e) {
            // Allow click events to propagate for expand functionality
            return true;
        }
    })[0];

    // Click to Expand
    popup.addEventListener('click', (e) => {
        if (e.target.closest('.video-popup__sound-toggle') || e.target.closest('.video-popup__close-individual') || e.target.closest('.video-popup__close-expanded')) return;
        if (popup.classList.contains('is-expanded')) return;

        // Disable dragging when expanded
        if (draggableInstance) draggableInstance.disable();

        popup.classList.add('is-expanded');
        overlay.classList.add('is-active');

        if (video) {
            video.muted = true;
            video.play().catch(e => console.warn('Video play failed:', e));
        }
        if (lenis) lenis.stop();
        setTimeout(() => ScrollTrigger.refresh(), 200);
    });

    // Dismiss logic (Permanent hide)
    dismissBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        gsap.to(popup, {
            opacity: 0,
            scale: 0.8,
            duration: 0.4,
            ease: 'power2.in',
            onComplete: () => {
                popup.style.display = 'none';
                ScrollTrigger.refresh();
            }
        });
    });

    // Sound Toggle Logic
    soundBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (video) {
            video.muted = !video.muted;
            const unmutedIcon = soundBtn.querySelector('.icon--unmuted');
            const mutedIcon = soundBtn.querySelector('.icon--muted');
            if (unmutedIcon && mutedIcon) {
                unmutedIcon.style.display = video.muted ? 'none' : 'block';
                mutedIcon.style.display = video.muted ? 'block' : 'none';
            }
        }
    });

    // Close Logic Expansion
    const closePopup = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        popup.classList.remove('is-expanded');
        overlay.classList.remove('is-active');
        if (video) video.muted = true;
        if (lenis) lenis.start();

        // Re-enable dragging when closed
        if (draggableInstance) draggableInstance.enable();

        setTimeout(() => ScrollTrigger.refresh(), 100);
    };

    closeBtn?.addEventListener('click', closePopup);
    overlay?.addEventListener('click', closePopup);

    // Hero Section Visibility Control (Matched to Floating Card Transition)
    // 플로팅 카드가 변하는 시점(스크롤 150px)에서 비디오 팝업도 사라지거나 나타나야 함
    const heroContent = document.querySelector('.content'); // Use same trigger element as floating card
    if (heroContent) {
        ScrollTrigger.create({
            trigger: heroContent,
            start: 'top top-=150', // Cards triggers here
            onEnter: () => {
                // Scrolled DOWN past 150px -> Hide Popup
                if (!popup.classList.contains('is-expanded')) {
                    gsap.to(popup, { opacity: 0, duration: 0.3, ease: 'power2.in', overwrite: true });
                }
            },
            onLeaveBack: () => {
                // Scrolled UP back into first 150px -> Show Popup
                gsap.to(popup, { opacity: 1, duration: 0.5, ease: 'power2.out', overwrite: true });
            }
        });
    }
};

// Hero Video Popup Show Logic (Called after intro)
const showHeroVideoPopup = () => {
    const popup = document.getElementById('hero-video-popup');
    const pin = document.querySelector('.pin');
    const floatingLeft = document.querySelector('.floating-left');

    // Check if we are past the hero section (matching ScrollTrigger logic at 150px)
    const isPastHero = window.scrollY > 150;

    // Elements to animate
    const elementsToShow = [];
    if (pin) elementsToShow.push(pin);
    if (floatingLeft) elementsToShow.push(floatingLeft);

    // Only show popup if we are in the hero section
    if (popup && !isPastHero) {
        elementsToShow.push(popup);
    } else if (popup) {
        // Ensure popup stays hidden if we are scrolled down
        gsap.set(popup, { opacity: 0 });
    }

    if (elementsToShow.length === 0) return;

    // display는 건드리지 않음 - CSS 원래 값 유지
    gsap.set(elementsToShow, {
        opacity: 0
    });

    // 단순 페이드인만
    gsap.to(elementsToShow, {
        opacity: 1,
        duration: 1.2,
        ease: 'power2.out',
        delay: 0.5,
        onComplete: () => {
            if (pin) pin.classList.add('is-visible');
            if (floatingLeft) floatingLeft.classList.add('visible');
        }
    });
};

// Unified Initialization
document.addEventListener('DOMContentLoaded', () => {
    initSmoothScrolling();
    // initHeroFadeOut(); // tlHero onComplete에서 호출되도록 유지
    initHeroVideoPopup();
    initLogoClick();
    initGoHome();
    initSoundToggle();
    initInfraToggle();
    initShowcaseCardsAnimation();
    initServiceEssenceAnimations();
    initFloatingCard();
    initInflationCharts();
    initTrustAnimations();
    initTrustLightingAnimation();
    initShowcaseAnimations();
    initHighlightEffect();
    initStickyHeader();
    initPreparednessStats();
    initDifficultyAnimations();
    initReviewCarousel();
    initSpecialtyAnimations();
    initNetworkAnimations();
    initColorTransition();
    initCountAnimation();
    initFeatureIconAnimations();
    initTimelineAnimation();
    initWarrantySystem();
    initBenefitTabs();
    initMembershipSlider();
    initFAQ();
    initFloatingVisibility();
    initBottomAnimations();
    initPrivacyToggle();
    initModalCloseButton();
    initFooterModals();
    initYoutubeAnimations();
    initStorytellingInflationGraph();
    initLeftFloating();

    // Ensure everything is calculated after all initializations
    setTimeout(() => {
        ScrollTrigger.refresh();
    }, 500);
});




// Inflation Graph Initialization
function initInflationGraph() {
    const mainSVG = document.querySelector('.mainSVG');
    if (!mainSVG) return;

    const box = document.querySelector('.box');
    const connector = document.querySelector('#connector');
    const dragger = document.querySelector('#dragger');
    const graphDot = document.querySelector('#graphDot');

    // Updated Selectors for the new box structure
    const boxLabelYear = document.querySelector('#boxLabel-Year');
    const boxLabelPrice = document.querySelector('#boxLabel-Price');
    const boxLabelSave = document.querySelector('#boxLabel-Save');

    const nullDot = document.querySelector('#nullDot');
    const graphLine = document.querySelector('#graphLine');
    const savingsLabels = document.querySelectorAll('#savingsMarkers text');

    let isPressed = false;
    const boxPos = { x: 0, y: 0 };

    // Data points from user image
    const years = ["현재", "5년", "10년", "15년", "20년"];
    const marketPrices = [420, 465, 529, 601, 683];
    const sonoPrice = 399;

    // Explicitly set initial position to line start (100, 370)
    gsap.set([dragger, graphDot, nullDot], {
        transformOrigin: '50% 50%',
        x: 0,
        y: 0
    });

    gsap.set(box, {
        x: 100,
        y: 370,
        transformOrigin: '0 0',
        scale: 0,
        opacity: 0
    });

    const tl = gsap.timeline({
        paused: true,
        onUpdate: updateGraph
    });

    tl.to([graphDot, dragger], {
        motionPath: {
            path: graphLine,
            align: graphLine,
            alignOrigin: [0.5, 0.5]
        },
        duration: 5,
        ease: "none"
    });

    function updateGraph() {
        // Prevent flickering by removing duration-based tweens in onUpdate
        const progress = tl.progress();
        const draggerX = gsap.getProperty(dragger, "x");
        const draggerY = gsap.getProperty(dragger, "y");

        // Use Set for instant updates
        gsap.set(box, {
            x: draggerX + 100,
            y: draggerY + 370
        });

        const yearIdx = Math.min(Math.floor(progress * 5), 4);
        boxLabelYear.textContent = years[yearIdx] + " 예상 물가";

        const currentPrice = gsap.utils.interpolate(marketPrices, progress);
        boxLabelPrice.textContent = Math.round(currentPrice).toLocaleString() + '만원';

        // Proximity highlight
        savingsLabels.forEach((label, i) => {
            const pointProgress = i * 0.25;
            const distance = Math.abs(progress - pointProgress);
            const isNear = distance < 0.05;

            // Only update if changed to avoid thrashing
            if (isNear !== label.isNear) {
                label.isNear = isNear;
                gsap.to(label, {
                    scale: isNear ? 1.4 : 1,
                    fill: isNear ? "#C5A065" : "#FFF",
                    duration: 0.3,
                    overwrite: true
                });
            }
        });
    }

    function updateTimeline() {
        const perc = gsap.getProperty(nullDot, "x") / 600;
        // Directly set progress to avoid Tween Fighting (Flickering)
        tl.progress(perc);
    }

    function graphPress() {
        isPressed = true;
        gsap.to(dragger, {
            duration: 0.4,
            attr: { r: 50 },
            fill: "rgba(255, 255, 255, 0.15)",
            ease: "power2.out"
        });
        gsap.to(box, {
            duration: 0.4,
            scale: 1,
            opacity: 1,
            ease: "back.out(1.7)"
        });
    }

    function graphRelease() {
        isPressed = false;
        gsap.to(dragger, {
            duration: 0.3,
            attr: { r: 40 },
            fill: "rgba(255, 255, 255, 0.05)",
            ease: "power2.out"
        });
        // Keep the box visible after interaction
        gsap.to(box, {
            duration: 0.4,
            scale: 1,
            opacity: 1,
            ease: "power2.out"
        });
    }

    // Interactive Hover Points reveal and click-to-glide behavior
    const hoverPoints = document.querySelectorAll('.hover-point');
    hoverPoints.forEach(p => {
        const bubble = p.querySelector('.hover-bubble');
        const index = parseInt(p.getAttribute('data-index'));

        p.addEventListener('mouseenter', () => {
            gsap.to(bubble, { duration: 0.3, opacity: 1, y: -5, ease: "power2.out" });
        });
        p.addEventListener('mouseleave', () => {
            gsap.to(bubble, { duration: 0.3, opacity: 0, y: 0, ease: "power2.in" });
        });

        // Click to glide to this point
        p.addEventListener('click', () => {
            if (autoPlayTween) autoPlayTween.kill();
            graphPress();

            gsap.to(nullDot, {
                duration: 1,
                x: index * 150, // Maps index (0-4) to nullDot X (0-600)
                ease: "power2.inOut",
                onUpdate: updateTimeline,
                onComplete: () => {
                    // Slight visual reset for dragger but keep box visible
                    gsap.to(dragger, { duration: 0.3, fill: "rgba(255, 255, 255, 0.05)" });
                }
            });
        });
    });

    let autoPlayTween;

    function startAutoPlay() {
        graphPress();
        autoPlayTween = gsap.to(nullDot, {
            duration: 5, // Slightly slower for better readability
            x: 600,
            ease: "power1.inOut",
            onUpdate: updateTimeline,
            onComplete: () => {
                gsap.delayedCall(1, graphRelease);
            }
        });
    }

    Draggable.create(nullDot, {
        type: 'x',
        trigger: dragger,
        bounds: { minX: 0, maxX: 600 },
        onPress: () => {
            if (autoPlayTween) autoPlayTween.kill();
            graphPress();
        },
        onDrag: updateTimeline,
        onRelease: graphRelease,
        onThrowUpdate: updateTimeline,
        zIndexBoost: false
    });

    const introTl = gsap.timeline({
        scrollTrigger: {
            trigger: ".section--inflation-graph",
            start: "top 75%",
        },
        onComplete: () => {
            gsap.delayedCall(0.5, startAutoPlay);
        }
    });

    // Drawing lines first, then showing markers
    introTl.from('#horizontalLinesGroup line', {
        duration: 0.8,
        scaleX: 0,
        opacity: 0,
        stagger: 0.05,
        ease: "power2.out"
    })
        .from('#graphTextGroup text', {
            duration: 0.6,
            opacity: 0,
            stagger: 0.05,
            ease: "power2.out"
        }, "-=0.4")
        // Explicit Left->Right Draw using fromTo
        .fromTo(graphLine, {
            strokeDasharray: 2000,
            strokeDashoffset: 2000
        }, {
            duration: 2.5,
            strokeDashoffset: 0,
            ease: "power2.inOut"
        }, "-=0.2")
        // Explicit Left->Right Draw using fromTo
        .fromTo('#sonoFixedLine', {
            strokeDasharray: 1000,
            strokeDashoffset: 1000
        }, {
            duration: 1.5,
            strokeDashoffset: 0,
            ease: "power2.out"
        }, "-=2")
        .from([graphDot, dragger], {
            duration: 0.8,
            opacity: 0,
            scale: 0,
            ease: "back.out(1.7)"
        }, "-=1")
        .to(['#hoverPoints', '#savingsMarkers', '#sonoFixedLabel'], {
            duration: 1.2,
            opacity: 1,
            stagger: 0.1,
            ease: "power2.in"
        }, "-=0.8");
}





/**
 * Left Floating Buttons Logic (Revision)
 * Handles KakaoTalk & Gift modals and visibility
 */
function initLeftFloating() {
    var floatingLeft = document.querySelector('.floating-left');
    var btnKakao = document.getElementById('btn-kakao-open');
    var btnGift = document.getElementById('btn-gift-open');
    var modalKakao = document.getElementById('modal-kakao-float');
    var modalGift = document.getElementById('modal-gift-float');
    var btnGiftTrigger = document.getElementById('btn-gift-consult-trigger');

    if (!floatingLeft) return;

    // 2. Local Modal Helpers
    // 2. Local Modal Helpers with GSAP Animations
    var openFloatModal = function (m) {
        if (!m) return;
        m.style.display = 'flex';
        document.body.style.overflow = 'hidden';

        var content = m.querySelector('.modal__content');
        var overlay = m.querySelector('.modal__overlay');

        // Initial state
        gsap.set(overlay, { opacity: 0 });
        gsap.set(content, { opacity: 0, y: 50, scale: 0.95 });

        // Animation
        m.classList.add('is-open');
        gsap.to(overlay, { duration: 0.4, opacity: 1, ease: 'power2.out' });
        gsap.to(content, {
            duration: 0.6,
            opacity: 1,
            y: 0,
            scale: 1,
            ease: 'back.out(1.7)',
            clearProps: 'transform' // Clear to prevent conflicts with other CSS
        });
    };

    var closeFloatModal = function (m) {
        if (!m) return;
        m.classList.remove('is-open');

        var content = m.querySelector('.modal__content');
        var overlay = m.querySelector('.modal__overlay');

        gsap.to(content, { opacity: 0, y: 50, scale: 0.95, duration: 0.4, ease: 'power2.in' });
        gsap.to(overlay, {
            duration: 0.4,
            opacity: 0,
            ease: 'power2.in',
            onComplete: function () {
                m.style.display = 'none';
                document.body.style.overflow = '';
            }
        });
    };

    // 1. Scroll-based Visibility (Mobile Only Logic)
    // 모바일에서 히어로 섹션을 벗어났을 때만 버튼이 보이도록 설정
    const updateMobileFloatingVisibility = () => {
        if (window.innerWidth <= 768) {
            if (window.scrollY > 150) {
                floatingLeft.classList.add('is-active-mobile');
            } else {
                floatingLeft.classList.remove('is-active-mobile');
            }
        } else {
            // 데스크탑에서는 항상 보이거나 다른 로직(showHeroVideoPopup)에 따름
            // 데스크탑에서 불필요하게 클래스가 남아있을 수 있으므로 제거
            floatingLeft.classList.remove('is-active-mobile');
        }
    };

    window.addEventListener('scroll', updateMobileFloatingVisibility);
    window.addEventListener('resize', updateMobileFloatingVisibility);
    updateMobileFloatingVisibility(); // Initial check

    var closeFloatModal = function (m) {
        if (!m) return;
        m.classList.remove('is-open');

        var content = m.querySelector('.modal__content');
        var overlay = m.querySelector('.modal__overlay');

        gsap.to(overlay, { duration: 0.3, opacity: 0, ease: 'power2.in' });
        gsap.to(content, {
            duration: 0.4,
            opacity: 0,
            y: 30,
            scale: 0.95,
            ease: 'power2.in',
            onComplete: function () {
                m.style.display = 'none';
                document.body.style.overflow = '';
            }
        });
    };

    // 3. Button Listeners
    if (btnKakao) {
        btnKakao.addEventListener('click', function (e) {
            e.preventDefault();
            openFloatModal(modalKakao);
        });
    }

    if (btnGift) {
        btnGift.addEventListener('click', function (e) {
            e.preventDefault();
            openFloatModal(modalGift);
        });
    }

    // 4. Close Listeners (Overlay & Close Button)
    var modals = [modalKakao, modalGift];
    modals.forEach(function (m) {
        if (!m) return;
        var cb = m.querySelector('.modal__close');
        var ov = m.querySelector('.modal__overlay');
        if (cb) cb.addEventListener('click', function () { closeFloatModal(m); });
        if (ov) ov.addEventListener('click', function () { closeFloatModal(m); });
    });

    // 5. Gift Modal -> Main Consultation Modal Link
    if (btnGiftTrigger) {
        btnGiftTrigger.addEventListener('click', function (e) {
            e.preventDefault();
            closeFloatModal(modalGift);

            // Re-use existing consultation modal logic
            var mainConsultModal = document.getElementById('modal-consultation');
            if (mainConsultModal) {
                setTimeout(function () {
                    mainConsultModal.style.display = 'flex';
                    mainConsultModal.classList.add('is-open');
                    document.body.style.overflow = 'hidden';
                }, 350);
            }
        });
    }
}

// --- New Inflation Graph Animation (ScrollTrigger) ---
function initNewInflationGraph() {
    const container = document.querySelector('.graph-container');
    const svg = document.querySelector('.inflation-svg');

    // Safety check
    if (!container || !svg) {
        console.warn('Inflation Graph elements not found');
        return;
    }

    console.log('Initializing Inflation Graph Animation...');

    // Elements
    const graphLine = svg.querySelector('#graphLine');
    const sonoFixedLine = svg.querySelector('#sonoFixedLine');
    const sonoFixedLabel = svg.querySelector('#sonoFixedLabel');
    const hoverPointsGroup = svg.querySelector('#hoverPoints'); // Parent Group
    const hoverPoints = svg.querySelectorAll('.hover-point');
    const bubbles = svg.querySelectorAll('.hover-bubble');
    const horizontalLines = svg.querySelectorAll('#horizontalLinesGroup line');
    const axisText = svg.querySelectorAll('#graphTextGroup text');

    // 1. Initial States (Hide everything first)
    if (graphLine) {
        const length = graphLine.getTotalLength();
        gsap.set(graphLine, {
            strokeDasharray: length,
            strokeDashoffset: length,
            opacity: 1
        });
    }

    if (sonoFixedLine) {
        gsap.set(sonoFixedLine, { scaleX: 0, transformOrigin: 'left center', opacity: 1 });
    }

    // Ensure Parent Group is visible
    if (hoverPointsGroup) {
        gsap.set(hoverPointsGroup, { attr: { opacity: 1 }, opacity: 1 });
    }

    if (sonoFixedLabel) {
        gsap.set(sonoFixedLabel, { autoAlpha: 0, y: 20 });
    }

    gsap.set(hoverPoints, { scale: 0, transformOrigin: 'center center', autoAlpha: 0 });
    gsap.set(bubbles, { autoAlpha: 0, scale: 0, transformOrigin: 'center bottom' });
    gsap.set(horizontalLines, { scaleX: 0, transformOrigin: 'left center', autoAlpha: 0 });
    gsap.set(axisText, { autoAlpha: 0, y: 10 });

    // 2. Timeline
    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: container,
            start: "top 75%",
            once: true,
            // markers: true // Debugging
        }
    });

    tl
        // Step 1: Background
        .to(horizontalLines, {
            scaleX: 1,
            opacity: 0.1,
            duration: 0.8,
            stagger: 0.1,
            ease: "power2.out"
        })
        .to(axisText, {
            opacity: 0.6,
            y: 0,
            duration: 0.6,
            stagger: 0.05,
            ease: "power2.out"
        }, "-=0.6")

        // Step 2: Lines
        .to(graphLine, {
            strokeDashoffset: 0,
            duration: 2,
            ease: "power2.inOut"
        }, "-=0.2")
        .to(sonoFixedLine, {
            scaleX: 1,
            duration: 1.5,
            ease: "power2.out"
        }, "<")

        // Step 3: Label
        .to(sonoFixedLabel, {
            autoAlpha: 1,
            y: 0,
            duration: 0.8,
            ease: "back.out(1.7)"
        }, "-=1")

        // Step 4: Points & Bubbles
        .to(hoverPoints, {
            autoAlpha: 1,
            scale: 1,
            duration: 0.5,
            stagger: 0.25,
            ease: "back.out(2)"
        }, "-=1.2")
        .to(bubbles, {
            autoAlpha: 1,
            scale: 1,
            duration: 0.4,
            stagger: 0.25,
            ease: "back.out(1.5)"
        }, "<+=0.1");

    // 3. Hover Interaction
    hoverPoints.forEach(point => {
        point.addEventListener('mouseenter', () => {
            gsap.to(point, { scale: 1.3, duration: 0.3, ease: 'back.out(1.7)' });
        });
        point.addEventListener('mouseleave', () => {
            gsap.to(point, { scale: 1, duration: 0.3, ease: 'power2.out' });
        });
    });
}
